import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '@store/hooks';
import { useTranslation } from 'react-i18next';
import { CREATE_DEVICE_PATH } from '@constants/routes';
import {
    DEVICE_LIST_IMAGE_SRC,
    DRIPSET_DEVICE_SRC,
    BED_IMAGE_SRC,
    DOOR_IMAGE_SRC,
    VOLUME_IMAGE_SRC,
} from '@constants/images';
import { IoClose, IoSend } from 'react-icons/io5';
import DownIcon from '@components/SvgComponents/DownIcon';
import UpIcon from '@components/SvgComponents/UpIcon';
import { Dropdown } from 'react-bootstrap';
import { FaFilter, FaBed, FaUserNurse } from 'react-icons/fa';
import { GoAlert } from 'react-icons/go';
import { MdOutlineModeEdit, MdOutlineWaterDrop, MdOutlineAccessTime } from 'react-icons/md';
import CloseIcon from '@components/SvgComponents/CloseIcon';
import { PiBatteryLowBold } from 'react-icons/pi';
import { FiZap } from 'react-icons/fi';
import { BsPlayCircleFill } from 'react-icons/bs';
import {
    User,
    SquareDashedMousePointer,
    Play,
    CircleAlert,
    BatteryCharging,
    Droplets,
    Gauge,
    Clock3,
    Pill,
    Cpu,
    Stethoscope,
} from 'lucide-react';
import DeviceModalPopUp from '@components/DeviceModalPopUpComponent';
import { GoAlertFill } from 'react-icons/go';
import { HOSPITAL, INDIVIDUAL, NURSE } from '@constants/constants';
import Spinner from 'react-bootstrap/Spinner';
import ModalPopUp from './ModalPopUpComponent';
import {
    generateUniqueTimestamp,
    getDataUsingUserId,
    nurseDeviceData,
    updateTable,
} from '@utils/sharedUtils';
import {
    setDeviceCardDetail,
    setTreeViewSelectedValue,
} from '@store/reducers/userSlice';
import DataLoader from './DataLoader';
import {
    fetchHospitalBlockData,
    hospitalBlockParentData,
} from '@utils/deviceUtils';
import { AttributeValue } from '@aws-sdk/client-dynamodb';
import Swal from 'sweetalert2';
import MyTreeView from './TreeView/TreeView';
import { startHourlyFlowRateSchedule } from '../utils/hourlyScheduleManager';
import {
    DRIP_FLOW_RATE_API,
    DRIP_INFUSION_TIME_API,
    FLOW_RATE_API,
    INFUSION_TIME_API,
    PUB_DRIP_FLOW_RATE_API,
    PUB_DRIP_INFUSION_TIME_API,
    PUB_FLOW_RATE_API,
    PUB_FLOW_RATE_WITH_HOUR_API,
    PUB_INFUSION_TIME_API,
    PUB_STOP_INFUSION_API,
    STOP_INFUSION_API,
    UPDATE_PATIENT_INFO_API,
} from '@constants/auth/Api';
import { API_KEY } from '@constants/AppConfig';
import SearchBar from './Layout/SearchBar';
import { useAuditLog } from '../contexts/AuditLogContext';
import { sendPushNotification, updateNotificationApi } from '@utils/notificationUtils';
import { USER_INFO_TABLE } from '@constants/dynamoDBTableName';
import Pagination from './Layout/Pagination';

// interface FormData {
//     type: string;
//     building?: string;
//     ward?: string;
//     room?: string;
// }

type CustomDropdownToggleProps = {
    children?: React.ReactNode;
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
};
const CustomDropdownToggle = React.forwardRef<
    HTMLButtonElement,
    CustomDropdownToggleProps
>(({ children, onClick }, ref) => (
    <button
        ref={ref}
        onClick={(e) => {
            e.preventDefault();
            onClick?.(e);
        }}
        className="btn !bg-primary text-white mr-5"
    >
        {children}
        <FaFilter className="ml-2" /> {/* Add your custom icon here */}
    </button>
));
interface HourlyStep {
    hour: number;
    setMins: number;
    flowRate: number;
}

interface FormData {
    type: string;
    timeDuration?: string;
    dripQuantity?: string;
    flowRate?: string;
    patientName?: string;
    medicineName?: string;
    hourlySteps?: HourlyStep[];
    setMins?: number;
}
interface FormDataDrip {
    type: string;
    timeDuration?: string;
    dripQuantity?: string;
    flowRate?: string;
    patientName?: string;
    medicineName?: string;
}

interface DeviceCardDetail {
    id: string;
    nurse_id?: string;
    hasControl?: boolean;
    device_id?: string;
    device_name?: string;
    patient_name?: string;
    medicine_name?: string;
    assigned?: string;
    deviceStatus: string;
    infusion_rate: number;
    volume: number;
    volume_consumed: number;
    time_duration: number;
    battery_percentage: number;
    battery_status?: string;
    T_L_Time: number;
    T_Qua: number;
    Sel_Qua: number;
    RPM: number;
    status: string;
    error_code: string;
    created_at: string;
    created_at_time: string;
    created_by: string;
    bed_no: string;
}

interface Block {
    name: string;
    parent_id: string;
    uuid: string;
}

type TreeViewDataType = Record<string, any>[];

interface DeviceListDetailPageProps {
    fetchAttributes: () => Promise<void>;
}

const MySwal = Swal;

const DeviceListDetailPage: React.FC<DeviceListDetailPageProps> = ({
    fetchAttributes,
}) => {
    const deviceCardList = useAppSelector(
        (state) => state.user.deviceCardDetails
    );
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    const isBatteryCharging = (status?: string) => {
        const normalized = String(status || '').trim().toLowerCase();
        return /charging\s*in\s*progress/.test(normalized);
    };
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [infusionLoading, setInfusionLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [formData, setFormData] = useState<FormData>({ type: '' });
    const [formDataDrip, setFormDataDrip] = useState<FormDataDrip>({
        type: '',
    });
    const [cardDetail, setCardDetail] = useState<DeviceCardDetail | null>(null);
    const [treeViewData, setTreeViewData] = useState<TreeViewDataType>([]);
    const [selectType, setSelectType] = useState('');
    const [isFirstModalOpen, setIsFirstModalOpen] = useState(false);
    const [isTakeOverModalOpen, setIsTakeOVerModalOpen] = useState(false);
    const [isInfusionButtonVisible, setIsInfusionButtonVisible] =
        useState(true);
    const [isBuildingExpanded, setIsBuildingExpanded] = useState(true);
    const [isWardExpanded, setIsWardExpanded] = useState(true);
    const [isInfusionStatusExpanded, setIsInfusionStatusExpanded] =
        useState(true);
    const [isDeviceExpanded, setIsDeviceExpanded] = useState(true);
    const [isInfusionDetailModalOpen, setIsInfusionDetailModalOpen] =
        useState(false);
    const [isInfusionModalOpen, setIsInfusionModalOpen] = useState(false);
    const [selectedInfusionInput, setSelectedInfusionInput] = useState('');
    const [selectedInfusionInputDrip, setSelectedInfusionInputDrip] =
        useState('');
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const [filterField, setFilterField] = useState('all device');
    const [sortField, setSortField] = useState('all infusion');
    const [sort, setSort] = useState('all infusion');
    const [filter, setFilter] = useState('all device');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [showTextModal, setShowTextModal] = useState(false);
    const [hospitalParentBlock, setHospitalParentBlock] = useState<Block[]>([]);
    // const userRole = useAppSelector((state) => state.signup.userRole);
    const userRole = useAppSelector((state) => state.login.loggedInUserRole);
    const loggedInUserTenantID = useAppSelector(
        (state) => state.login.loggedInUserTenantID
    );
    const loggedInUserID = useAppSelector(
        (state) => state.login.loggedInUserID
    );
    const loggedInUserCognitoUuid = useAppSelector(
        (state) => state.login.loggedInUserCognitoUuid
    );

    const loggedInHospitalId = useAppSelector(
        (state) => state.login.loggedInHospitalId
    );
    const userName = useAppSelector((state) => state.login.loggedInUserName);
    const loggedInUserEmail = useAppSelector(
        (state) => state.login.loggedInUserEmail
    );
    const treeViewSelectedValue = useAppSelector(
        (state) => state.user.treeViewSelectedValue
    );
    const accessToken = useAppSelector((state) => state.user.accessToken);

    const [errorSummaryText, setErrorSummaryText] = useState('');
    const [isSubmittingErrorSummary, setIsSubmittingErrorSummary] = useState(false);

    const handleNurseSubmitErrorSummary = async () => {
        if (!errorSummaryText.trim() || !cardDetail?.device_id) return;
        setIsSubmittingErrorSummary(true);
        try {
            await updateNotificationApi(
                {
                    device_id: cardDetail.device_id,
                    nurse_summary: errorSummaryText.trim(),
                    nurse_name: userName || 'Nurse',
                },
                accessToken
            );
            Swal.fire({
                position: 'top-end',
                icon: 'success',
                title: 'Error summary submitted successfully!',
                showConfirmButton: false,
                timer: 3000,
                toast: true,
            });
            setErrorSummaryText('');
        } catch (err: any) {
            console.error('Failed to submit error summary:', err);
            Swal.fire({
                position: 'top-end',
                icon: 'error',
                title: err.message || 'Failed to submit error summary',
                showConfirmButton: false,
                timer: 3000,
                toast: true,
            });
        } finally {
            setIsSubmittingErrorSummary(false);
        }
    };

    const [selectedParent, setSelectedParent] = useState('');

    const { addLog } = useAuditLog();

    const handleParentChange = (
        event: React.ChangeEvent<HTMLSelectElement>
    ) => {
        setSelectedParent(event.target.value);
    };

    const maxValue = cardDetail?.Sel_Qua || cardDetail?.volume || 0;

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        let { name, value } = event.target;

        // Allow only numbers and a single decimal point
        if (!/^\d*\.?\d*$/.test(value)) {
            return;
        }

        // Convert value to number
        const numericValue = parseFloat(value);

        if (numericValue > maxValue) {
            // If the new value is greater than the max, set the input value to the max
            event.target.value = maxValue.toString();
            handleSelectChange({
                ...event,
                target: { ...event.target, name, value: maxValue.toString() },
            });
            return;
        }

        // Update the state only if valid
        handleSelectChange({
            ...event,
            target: { ...event.target, name, value },
        });
    };

    const handleSelectChange = (
        e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>
    ) => {
        const { name, value } = e.target;
        setFormData((prevState) => {
            const newState = { ...prevState, [name]: value };
            if (name === 'type' && value === 'hourly_flow_rate' && (!prevState.hourlySteps || prevState.hourlySteps.length === 0)) {
                newState.hourlySteps = [{ hour: 1, setMins: 60, flowRate: 1.0 }];
            }
            return newState;
        });
        if (name === 'type') {
            setSelectedInfusionInput(value);
            if (value !== 'drip_quantity') {
                setSelectedInfusionInputDrip('');
                setFormDataDrip({
                    type: '',
                    timeDuration: '',
                    dripQuantity: '',
                    flowRate: '',
                    patientName: '',
                    medicineName: '',
                });
            }
        }
    };

    const handleAddHourlyStep = () => {
        setFormData((prev) => {
            const currentSteps = prev.hourlySteps || [{ hour: 1, setMins: 60, flowRate: 1.0 }];
            const nextHour = currentSteps.length + 1;
            const totalVtbi = Number(cardDetail?.Sel_Qua || cardDetail?.volume || 0);
            const consumedVolume = currentSteps.reduce(
                (sum, s) => sum + Number(s.setMins || 0) * Number(s.flowRate || 0),
                0
            );
            const defaultFlowRate = 1.0;
            let defaultSetMins = 60;

            if (totalVtbi > 0) {
                const remainingVolume = Math.max(0, totalVtbi - consumedVolume);
                if (remainingVolume <= 0) {
                    return prev;
                }
                const maxMins = Math.floor(remainingVolume / defaultFlowRate);
                if (maxMins < 1) {
                    return prev;
                }
                defaultSetMins = Math.min(60, maxMins);
            }

            return {
                ...prev,
                hourlySteps: [
                    ...currentSteps,
                    { hour: nextHour, setMins: defaultSetMins, flowRate: defaultFlowRate },
                ],
            };
        });
    };

    const handleRemoveHourlyStep = (index: number) => {
        setFormData((prev) => {
            const currentSteps = prev.hourlySteps || [];
            if (currentSteps.length <= 1) return prev;
            const updated = currentSteps.filter((_, idx) => idx !== index).map((step, idx) => ({
                ...step,
                hour: idx + 1,
            }));
            return {
                ...prev,
                hourlySteps: updated,
            };
        });
    };

    const handleHourlyStepChange = (index: number, field: 'setMins' | 'flowRate', value: number) => {
        setFormData((prev) => {
            const currentSteps = [...(prev.hourlySteps || [{ hour: 1, setMins: 60, flowRate: 1.0 }])];
            if (currentSteps[index]) {
                const totalVtbi = Number(cardDetail?.Sel_Qua || cardDetail?.volume || 0);
                let newValue = value;

                if (field === 'flowRate') {
                    // Snap flow rate strictly to 0.5 steps (e.g., 0.5, 1.0, 1.5, 2.0...)
                    newValue = Math.round(value * 2) / 2;
                    newValue = Math.max(0.5, Math.min(7.0, newValue));

                    if (totalVtbi > 0) {
                        const otherVolume = currentSteps.reduce((sum, step, idx) => {
                            if (idx === index) return sum;
                            return sum + Number(step.setMins || 0) * Number(step.flowRate || 0);
                        }, 0);

                        const remainingForThisStep = Math.max(0, totalVtbi - otherVolume);
                        const mins = Number(currentSteps[index].setMins || 60);
                        if (mins > 0) {
                            const maxRawRate = remainingForThisStep / mins;
                            // Floor to nearest 0.5 step so flow rate never exceeds remaining volume
                            const maxAllowedRate = Math.floor(maxRawRate * 2) / 2;
                            newValue = Math.min(newValue, Math.max(0.5, maxAllowedRate));
                        }
                    }
                } else if (field === 'setMins') {
                    // Duration in minutes must be a positive integer
                    newValue = Math.max(1, Math.round(value));

                    if (totalVtbi > 0) {
                        const otherVolume = currentSteps.reduce((sum, step, idx) => {
                            if (idx === index) return sum;
                            return sum + Number(step.setMins || 0) * Number(step.flowRate || 0);
                        }, 0);

                        const remainingForThisStep = Math.max(0, totalVtbi - otherVolume);
                        const rate = Number(currentSteps[index].flowRate || 1.0);
                        if (rate > 0) {
                            const maxAllowedMins = Math.floor(remainingForThisStep / rate);
                            newValue = Math.min(newValue, Math.max(1, maxAllowedMins));
                        }
                    }
                }

                currentSteps[index] = {
                    ...currentSteps[index],
                    [field]: newValue,
                };
            }
            return {
                ...prev,
                hourlySteps: currentSteps,
            };
        });
    };

    const handleSelectChangeDrip = (
        e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>
    ) => {
        const { name, value } = e.target;
        setFormDataDrip((prevState) => ({ ...prevState, [name]: value }));
        if (name === 'type') {
            setSelectedInfusionInputDrip(value);
        }
    };

    const updatePatientAndMedicineName = async (patientName: string, medicineName: string) => {
        if (!cardDetail || !cardDetail.device_id) return;
        try {
            const requestBody = {
                device_id: cardDetail.device_id,
                patient_name: patientName,
                medicine_name: medicineName
            };
            const response = await fetch(UPDATE_PATIENT_INFO_API, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': API_KEY,
                    'access-token': accessToken
                },
                body: JSON.stringify(requestBody)
            });

        } catch (error) {
            console.error(error);
        }
    };

    const handleOpenViewInfusionModal = () => {
        setIsFirstModalOpen(false);
        setIsInfusionDetailModalOpen(true);
        setIsInfusionButtonVisible(false);
    };
    const handleTakeOverConfirmationModal = () => {
        setIsFirstModalOpen(false);
        setShowModal(true);
    };
    const handleOpenTakeOverModal = () => {
        setIsFirstModalOpen(false);
        setIsTakeOVerModalOpen(true);
    };
    const handleOpenInfusionModal = () => {
        setIsFirstModalOpen(false);
        setIsTakeOVerModalOpen(false);
        setIsInfusionDetailModalOpen(true);
        setIsInfusionButtonVisible(true);
    };
    const handleStartInfusionModal = async (cardDetail: any) => {
        if (
            userRole.toLowerCase() == HOSPITAL.toLowerCase() ||
            userRole.toLowerCase() == NURSE.toLowerCase()
        ) {
            try {
                const data = await hospitalBlockParentData(loggedInHospitalId);
                setHospitalParentBlock(data);
            } catch (error: any) {
                let errorMessage;

                switch (error.name) {
                    case 'ValidationException':
                        errorMessage =
                            'Validation error: Please check your request parameters.';
                        break;
                    case 'ProvisionedThroughputExceededException':
                        errorMessage =
                            'Provisioned throughput exceeded: Please try again later.';
                        break;
                    case 'ResourceNotFoundException':
                        errorMessage =
                            'Resource not found: Please check your table name.';
                        break;
                    case 'InternalServerError':
                        errorMessage =
                            'Internal server error: Please try again later.';
                        break;
                    case 'ServiceUnavailable':
                        errorMessage =
                            'Service unavailable: Please try again later.';
                        break;
                    default:
                        errorMessage = `Unknown error: ${error.message || error}`;
                }
                MySwal.fire({
                    position: 'top-end', // Position the toast in the top-right corner
                    icon: 'error',
                    title: errorMessage,
                    showConfirmButton: false,
                    timer: 3000,
                    toast: true,
                    customClass: {
                        popup: '!text-red !bg-light_red', // Add custom class to the popup
                    },
                    width: 'auto',
                });
            }
        }

        if (cardDetail) {
            const T_Qua = Number(cardDetail.T_Qua);
            const T_L_Time = Number(cardDetail.T_L_Time);
            const RPM = Number(cardDetail.RPM);

            if (T_Qua !== 0) {
                setSelectedInfusionInput('drip_quantity');
                setFormData((prevFormData) => ({
                    ...prevFormData,
                    type: 'drip_quantity',
                    dripQuantity: cardDetail.T_Qua,
                }));

                if (T_L_Time !== 0) {
                    setSelectedInfusionInputDrip('time_duration');
                    setFormDataDrip((prevFormData) => ({
                        ...prevFormData,
                        type: 'time_duration',
                        timeDuration: cardDetail.T_L_Time,
                    }));
                } else if (RPM !== 0) {
                    setSelectedInfusionInputDrip('flow_rate');
                    setFormDataDrip((prevFormData) => ({
                        ...prevFormData,
                        type: 'flow_rate',
                        flowRate: cardDetail.RPM,
                    }));
                }
            } else {
                // Handle case where T_Qua is zero and only one of T_L_Time or RPM has a value
                if (T_L_Time !== 0) {
                    setFormData((prevFormData) => ({
                        ...prevFormData,
                        type: 'time_duration',
                        timeDuration: cardDetail.T_L_Time,
                    }));
                    setSelectedInfusionInput('time_duration');
                } else if (RPM !== 0) {
                    setFormData((prevFormData) => ({
                        ...prevFormData,
                        type: 'flow_rate',
                        flowRate: cardDetail.RPM,
                    }));
                    setSelectedInfusionInput('flow_rate');
                }
            }
        }
        setIsFirstModalOpen(false);
        setIsTakeOVerModalOpen(false);
        setIsInfusionDetailModalOpen(false);
        setIsInfusionButtonVisible(true);
        setIsInfusionModalOpen(true);
    };

    const handleCloseSecondModal = () => {
        setIsInfusionDetailModalOpen(false);
    };
    const handleCloseTakeOverModal = () => {
        setIsTakeOVerModalOpen(false);
    };
    const handleCloseInfusionModal = () => {
        setIsInfusionModalOpen(false);
        setIsInfusionDetailModalOpen(true);
    };

    const filteredUsers = useMemo(() => {
        let filtered = deviceCardList;

        if (filtered && filtered.length != 0) {
            if (searchTerm) {
                filtered = filtered.filter((user) =>
                    user.device_id
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase())
                );
            }
            if (filterField == 'all device') {
                filtered;
            } else {
                // filtered = filtered.filter((device) =>
                //     device.deviceStatus
                //         ?.toLowerCase()
                //         .includes(filterField.toLowerCase())
                // );
                filtered = filtered.filter(
                    (device) =>
                        device.deviceStatus?.toLowerCase() === filterField.toLowerCase()
                );
            }
        }


        if (cardDetail && isInfusionDetailModalOpen) {
            const updatedDevice = deviceCardList?.find(
                (device) => device.device_id === cardDetail?.device_id
            );

            if (updatedDevice) {
                setCardDetail(updatedDevice);
            }
        }
        return filtered;
    }, [searchTerm, filterField, deviceCardList, cardDetail]);

    const sortedData = useMemo(() => {
        const users = filteredUsers ?? [];
        if (users.length === 0) return [];

        let sorted = [...users];
        if (sortField.toLowerCase() === 'all infusion') {
            sorted;
        } else if (sortField === 'Infusion In Progress') {
            sorted = sorted.filter((device) =>
                device.status === 'Infusion In Progress' || device.status === 'Infusion Started'
            );
        } else if (sortField === 'Infusion Complete') {
            sorted = sorted.filter((device) =>
                device.status === 'Infusion Complete' || device.status === 'Infusion Completed'
            );
        } else {
            sorted = sorted.filter((device) =>
                device.status
                    ?.toLowerCase()
                    .includes(sortField.toLowerCase())
            );
        }
        // Always put active devices first
        sorted.sort((a, b) => {
            const aActive = a.deviceStatus?.toLowerCase() === 'active' ? 0 : 1;
            const bActive = b.deviceStatus?.toLowerCase() === 'active' ? 0 : 1;
            return aActive - bActive;
        });
        return sorted;
    }, [filteredUsers, sortField]);

    const paginatedUsers = useMemo(() => {
        const startIndex = (currentPage - 1) * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        return sortedData.slice(startIndex, endIndex);
    }, [sortedData, currentPage, rowsPerPage]);

    const totalPages = filteredUsers ? Math.ceil(filteredUsers.length / rowsPerPage) : 0;
    const hasPaginatedUsers = paginatedUsers.length > 0;

    const handleSortChange = (event: string) => {
        setSort(event);
    };
    const handleFilterChange = (value: string) => {
        setFilter(value);
        // setStatusFilter(value);
    };
    const toggleBuildingExpand = () => {
        setIsBuildingExpanded(!isBuildingExpanded);
    };
    const handleAddData = () => {
        setShowModal(true);
    };
    const handleAddDevice = () => {
        navigate(CREATE_DEVICE_PATH);
    };
    const toggleWardExpand = () => {
        setIsWardExpanded(!isWardExpanded);
    };
    const toggleInfusionStatusExpand = () => {
        setIsInfusionStatusExpanded(!isInfusionStatusExpanded);
    };
    const toggleDeviceExpand = () => {
        setIsDeviceExpanded(!isDeviceExpanded);
    };
    const handleCloseFirstModal = () => {
        setIsFirstModalOpen(false);
    };
    const handleCartClick = (device: any) => {
        setCardDetail(device);
        dispatch(setTreeViewSelectedValue(device.bed_no));
        if (
            userRole.toLowerCase() != INDIVIDUAL.toLowerCase() &&
            userRole.toLowerCase() != HOSPITAL.toLowerCase()
        ) {
            if (
                device.nurse_id == loggedInUserCognitoUuid &&
                device.hasControl == true
            ) {
                handleOpenInfusionModal();
            } else {
                setIsFirstModalOpen(true);
            }
        } else {
            handleOpenInfusionModal();
        }
    };
    const handleCartAlertClick = () => {
        setShowTextModal(true);
    };

    const getStatusClass = (status: string) => {
        if (!status) {
            return ''; // Return an empty string if status is undefined or null
        }

        switch (status.toLowerCase().trim()) {
            case 'no infusion':
                return 'text-white bg-action_color inline-block rounded-full mt-4 px-2 py-1';
            case 'infusion progress':
                return 'text-white bg-status_warning inline-block rounded-full mt-4 px-2 py-1';
            case 'infusion completed':
                return 'text-white bg-status_success inline-block rounded-full mt-4 px-2 py-1';
            case 'infusion stopped':
                return 'text-white bg-status_danger inline-block rounded-full mt-4 px-2 py-1';
            default:
                return '';
        }
    };

    const handleCloseModal = (event: any) => {
        event.stopPropagation();
        setIsDropdownVisible(false);
    };

    const handleClear = (event: any) => {
        event.stopPropagation();
        setSortField('all infusion');
        setSort('all infusion');
        setFilterField('all device');
        setFilter('all device');
        handleCloseModal(event);
    };

    const handleSave = (event: any) => {
        event.stopPropagation();
        setSortField(sort);
        setFilterField(filter);
        handleCloseModal(event);
    };

    const handleDropdownToggle = (event: any) => {
        event.stopPropagation();
        // Prevent the default behavior to keep the dropdown open
        if (event && event.target) {
            // Check if the event target has the data-click-type attribute
            const isButtonClick = event.target.getAttribute('data-click-type');

            if (
                isButtonClick === 'btn-clear' ||
                isButtonClick === 'btn-save' ||
                isButtonClick === 'btn-close'
            ) {
                setIsDropdownVisible(false);
            } else {
                // setIsDropdownVisible(true);
                setIsDropdownVisible(!isDropdownVisible);
            }
        }
    };

    const fetchData = async () => {
        try {
            const hospitalBlock =
                await fetchHospitalBlockData(loggedInUserTenantID); // Await the promise
            setTreeViewData(hospitalBlock); // Set the fetched data to the state
        } catch (error) {
            console.error('Error fetching hospital block data:', error);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event: any) => {
            if (
                !event.target.closest('.dropdown-menu') &&
                !event.target.closest('.dropdown-toggle')
            ) {
                setIsDropdownVisible(false);
            }
        };

        if (cardDetail && cardDetail.patient_name) {
            setFormData((prevFormData) => ({
                ...prevFormData,
                patientName: cardDetail.patient_name || '--',

            }));
        }
        if (cardDetail && cardDetail.medicine_name) {
            setFormData((prevFormData) => ({
                ...prevFormData,
                medicineName: cardDetail.medicine_name || '--',

            }));
        }

        if (userRole.toLowerCase() != INDIVIDUAL.toLowerCase()) {
            fetchData();
        }

        document.addEventListener('click', handleClickOutside);

        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [cardDetail, userRole, INDIVIDUAL]);

    const updateDeviceControl = async () => {
        try {
            setLoading(true);
            // const formattedDateTime = new Date().toISOString();
            const formattedDateTime = generateUniqueTimestamp();

            if (cardDetail) {
                const res = await nurseDeviceData(cardDetail.id);
                const controlNurse = res.filter(
                    (item) => item.user_id?.S == cardDetail.nurse_id
                );
                const currentNurse = res.filter(
                    (item) => item.user_id?.S == loggedInUserID
                );

                if (controlNurse && currentNurse) {
                    const data = controlNurse[0];
                    const currentNursedata = currentNurse[0];

                    const attributeNames = {
                        '#has_control': 'has_control',
                        '#modified_by': 'modified_by',
                        '#modified_at': 'modified_at',
                    };

                    const attributeValues = {
                        ':has_control': { BOOL: false },
                        ':modified_by': { S: loggedInUserTenantID },
                        ':modified_at': { S: formattedDateTime },
                    };

                    const currentNurseAttributeValues = {
                        ':has_control': { BOOL: true },
                        ':modified_by': { S: loggedInUserTenantID },
                        ':modified_at': { S: formattedDateTime },
                    };

                    const updateExpression =
                        'SET #has_control = :has_control, #modified_by = :modified_by, #modified_at = :modified_at';

                    await updateTable(
                        'nurse_device',
                        { uuid: { S: data.uuid?.S } },
                        updateExpression,
                        attributeNames,
                        attributeValues
                    );
                    await updateTable(
                        'nurse_device',
                        { uuid: { S: currentNursedata.uuid?.S } },
                        updateExpression,
                        attributeNames,
                        currentNurseAttributeValues
                    ).then(() => {
                        if (deviceCardList) {
                            const updatedData = deviceCardList.map((item) =>
                                item.id === cardDetail.id
                                    ? {
                                        ...item,
                                        nurse_id: currentNursedata.uuid?.S,
                                        assigned: userName,
                                    }
                                    : item
                            );

                            dispatch(setDeviceCardDetail(updatedData));
                        }
                    });
                    await sendPushNotification({ user_id: cardDetail.nurse_id, type: 'take_over', device_id: cardDetail.device_id, reason: '', assigned_by: loggedInUserID });
                    addLog(
                        'Control of the device has been taken over',
                        currentNursedata.uuid?.S ? currentNursedata.uuid?.S : '',
                        'Nurse Device',
                        loggedInUserID,
                        { 'Device': `${cardDetail?.device_name || ''} (${cardDetail?.device_id || ''})`, 'Taken over by': loggedInUserEmail },
                        window.location.hostname
                    );
                }
            }
        } catch (error) {
            console.error('Error updating status:', error);
        } finally {
            setLoading(false);
            setShowModal(false);
            setIsTakeOVerModalOpen(true);
        }
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        setInfusionLoading(true);

        let apiEndpoint = '';
        let requestBody = {};
        let apiEndpoint_1 = '';
        let requestBody_1 = {};

        const formElement = event.target as HTMLFormElement;
        const formValue = new FormData(formElement); // Get the form data

        // Extract the device_id value
        const deviceId = formValue.get('device_id') as string | null;

        // Update patient and medicine names first
        if (formData.patientName || formData.medicineName) {
            await updatePatientAndMedicineName(
                formData.patientName || '',
                formData.medicineName || ''
            );
        }

        // If no type is selected, just update patient/medicine names and close modal
        if (!formData.type) {
            setInfusionLoading(false);
            setIsInfusionModalOpen(false);
            await fetchAttributes();

            // Determine which fields changed
            const originalPatientName = cardDetail?.patient_name || '';
            const originalMedicineName = cardDetail?.medicine_name || '';
            const patientNameChanged = formData.patientName !== originalPatientName;
            const medicineNameChanged = formData.medicineName !== originalMedicineName;

            // Show appropriate success message
            let successMessage = '';
            if (patientNameChanged && medicineNameChanged) {
                successMessage = 'Patient name and Medicine name updated successfully';
            } else if (patientNameChanged) {
                successMessage = 'Patient name updated successfully';
            } else if (medicineNameChanged) {
                successMessage = 'Medicine name updated successfully';
            } else {
                successMessage = 'Information updated successfully';
            }

            MySwal.fire({
                position: 'top-end',
                icon: 'success',
                title: successMessage,
                showConfirmButton: false,
                timer: 3000,
                toast: true,
                customClass: {
                    popup: 'text-green bg-light_green',
                },
                width: 'auto',
            });
            return;
        }

        // Determine the API endpoint based on form type
        switch (formData.type) {
            case 'flow_rate':
                apiEndpoint = FLOW_RATE_API;
                requestBody = {
                    device_id: deviceId,
                    RPM: formData.flowRate,
                    patient_name:
                        formData.patientName != '' ? formData.patientName : '',
                    medicine_name:
                        formData.medicineName != '' ? formData.medicineName : '',
                    bed_no: treeViewSelectedValue,
                    Volume:
                        cardDetail && cardDetail.volume_consumed
                            ? cardDetail.volume_consumed
                            : 0,
                    current_rpm:
                        cardDetail && cardDetail.RPM ? cardDetail.RPM : 0,
                    current_t_qua: 0,
                    current_t_l_time: 0,
                };
                apiEndpoint_1 = PUB_FLOW_RATE_API;
                requestBody_1 = {
                    device_id: deviceId,
                    RPM: formData.flowRate,
                };
                break;

            case 'hourly_flow_rate': {
                const steps = formData.hourlySteps && formData.hourlySteps.length > 0
                    ? formData.hourlySteps
                    : [{ hour: 1, setMins: 60, flowRate: 1.0 }];
                const totalVtbi = Number(cardDetail?.Sel_Qua || cardDetail?.volume || 0);
                const totalProgVol = steps.reduce((acc, curr) => acc + Number(curr.setMins || 0) * Number(curr.flowRate || 0), 0);
                if (totalVtbi > 0 && totalProgVol > totalVtbi) {
                    setInfusionLoading(false);
                    MySwal.fire({
                        icon: 'error',
                        title: 'Volume Exceeded',
                        text: `Programmed volume (${totalProgVol.toFixed(1)} ml) cannot exceed Total Volume (${totalVtbi} ml).`,
                    });
                    return;
                }
                const totalSetMins = steps.reduce((acc, curr) => acc + Number(curr.setMins || 0), 0);
                const initialRPM = steps[0]?.flowRate || 1.0;
                const initialSetMins = steps[0]?.setMins || 60;

                apiEndpoint = FLOW_RATE_API;
                requestBody = {
                    device_id: deviceId,
                    RPM: initialRPM,
                    T_L_Time: initialSetMins,
                    patient_name:
                        formData.patientName != '' ? formData.patientName : '',
                    medicine_name:
                        formData.medicineName != '' ? formData.medicineName : '',
                    bed_no: treeViewSelectedValue,
                    Volume:
                        cardDetail && cardDetail.volume_consumed
                            ? cardDetail.volume_consumed
                            : 0,
                    current_rpm:
                        cardDetail && cardDetail.RPM ? cardDetail.RPM : 0,
                    current_t_qua: 0,
                    current_t_l_time: 0,
                    hourly_steps: steps,
                    set_mins: totalSetMins,
                };
                apiEndpoint_1 = PUB_FLOW_RATE_WITH_HOUR_API;
                requestBody_1 = {
                    device_id: deviceId,
                    RPM: initialRPM,
                    T_L_Time: initialSetMins,
                    hourly_steps: steps,
                    set_mins: totalSetMins,
                };
                break;
            }

            case 'time_duration': {
                // Round RPM to nearest 0.5 step and recalculate T_L_Time
                const timeDurVolume = Number(cardDetail?.Sel_Qua || cardDetail?.volume || 0);
                const timeDurRawRPM = timeDurVolume / Number(formData.timeDuration);
                const timeDurRoundedRPM = Math.round(timeDurRawRPM * 2) / 2; // round to nearest 0.5
                const timeDurAdjusted = timeDurRoundedRPM > 0 ? Math.round(timeDurVolume / timeDurRoundedRPM) : Number(formData.timeDuration);

                apiEndpoint = INFUSION_TIME_API;
                requestBody = {
                    device_id: deviceId,
                    T_L_Time: timeDurAdjusted,
                    patient_name:
                        formData.patientName != '' ? formData.patientName : '',
                    medicine_name:
                        formData.medicineName != '' ? formData.medicineName : '',
                    bed_no: treeViewSelectedValue,
                    Volume:
                        cardDetail && cardDetail.volume_consumed
                            ? cardDetail.volume_consumed
                            : 0,
                    current_rpm: 0,
                    current_t_qua: 0,
                    current_t_l_time:
                        cardDetail && cardDetail.T_L_Time
                            ? cardDetail.T_L_Time
                            : 0,
                };
                apiEndpoint_1 = PUB_INFUSION_TIME_API;
                requestBody_1 = {
                    device_id: deviceId,
                    T_L_Time: timeDurAdjusted,
                };
                break;
            }

            case 'drip_quantity':
                if (formDataDrip.type === 'flow_rate') {
                    apiEndpoint = DRIP_FLOW_RATE_API;
                    requestBody = {
                        device_id: deviceId,
                        RPM: formDataDrip.flowRate,
                        T_Qua: formData.dripQuantity,
                        patient_name:
                            formData.patientName != ''
                                ? formData.patientName
                                : '',
                        medicine_name:
                            formData.medicineName != '' ? formData.medicineName : '',
                        bed_no: treeViewSelectedValue,
                        Volume:
                            cardDetail && cardDetail.volume_consumed
                                ? cardDetail.volume_consumed
                                : 0,
                        current_rpm:
                            cardDetail && cardDetail.RPM ? cardDetail.RPM : 0,
                        current_t_qua:
                            cardDetail && cardDetail.T_Qua
                                ? cardDetail.T_Qua
                                : 0,
                        current_t_l_time: 0,
                    };
                    apiEndpoint_1 = PUB_DRIP_FLOW_RATE_API;
                    requestBody_1 = {
                        device_id: deviceId,
                        RPM: formDataDrip.flowRate,
                        T_Qua: formData.dripQuantity,
                    };
                } else if (formDataDrip.type === 'time_duration') {
                    // Round RPM to nearest 0.5 step and recalculate T_L_Time for drip
                    const dripVolume = Number(formData.dripQuantity || 0);
                    const dripRawRPM = dripVolume / Number(formDataDrip.timeDuration);
                    const dripRoundedRPM = Math.round(dripRawRPM * 2) / 2; // round to nearest 0.5
                    const dripAdjustedTime = dripRoundedRPM > 0 ? Math.round(dripVolume / dripRoundedRPM) : Number(formDataDrip.timeDuration);

                    apiEndpoint = DRIP_INFUSION_TIME_API;
                    requestBody = {
                        device_id: deviceId,
                        T_L_Time: dripAdjustedTime,
                        T_Qua: formData.dripQuantity,
                        patient_name:
                            formData.patientName != ''
                                ? formData.patientName
                                : '',

                        medicine_name:
                            formData.medicineName != '' ? formData.medicineName : '',
                        bed_no: treeViewSelectedValue,
                        Volume:
                            cardDetail && cardDetail.volume_consumed
                                ? cardDetail.volume_consumed
                                : 0,
                        current_rpm: 0,
                        current_t_qua:
                            cardDetail && cardDetail.T_Qua
                                ? cardDetail.T_Qua
                                : 0,
                        current_t_l_time:
                            cardDetail && cardDetail.T_L_Time
                                ? cardDetail.T_L_Time
                                : 0,
                    };
                    apiEndpoint_1 = PUB_DRIP_INFUSION_TIME_API;
                    requestBody_1 = {
                        device_id: deviceId,
                        T_L_Time: dripAdjustedTime,
                        T_Qua: formData.dripQuantity,
                    };
                }
                break;

            default:
                // Unknown type - just update patient/medicine names
                setInfusionLoading(false);
                setIsInfusionModalOpen(false);
                return;
        }

        // Call the appropriate API
        try {
            if (apiEndpoint != '') {
                const response = await fetch(apiEndpoint, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'x-api-key': API_KEY, // Replace with your actual API key
                        'access-token': accessToken, // Use token if needed
                    },
                    body: JSON.stringify(requestBody),
                });

                const result = await response.json();

                // if (response.ok) {
                //     // console.log('Form submitted successfully:', result);
                // } else {
                //     // console.error('Error submitting form:', result.message);
                // }
            }
            if (apiEndpoint_1 != '') {
                const response_1 = await fetch(apiEndpoint_1, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'x-api-key': API_KEY, // Replace with your actual API key
                        'access-token': accessToken, // Use token if needed
                    },
                    body: JSON.stringify(requestBody_1),
                });

                const result_1 = await response_1.json();

                // if (response_1.ok) {
                //     // console.log('Form submitted successfully:', result_1);
                // } else {
                //     // console.error('Error submitting form:', result_1.message);
                // }
            }

            switch (formData.type) {
                case 'flow_rate':
                    setCardDetail((prevState) =>
                        prevState
                            ? {
                                ...prevState,
                                RPM: Number(formData.flowRate),
                                T_L_Time: 0,
                                T_Qua: 0,
                                status: 'infusion progress',
                            }
                            : null
                    );
                    break;

                case 'hourly_flow_rate': {
                    const steps = formData.hourlySteps && formData.hourlySteps.length > 0
                        ? formData.hourlySteps
                        : [{ hour: 1, setMins: 60, flowRate: 1.0 }];
                    const totalSetMins = steps.reduce((acc, curr) => acc + Number(curr.setMins || 0), 0);
                    const initialRPM = steps[0]?.flowRate || 1.0;
                    setCardDetail((prevState) =>
                        prevState
                            ? {
                                ...prevState,
                                RPM: Number(initialRPM),
                                T_L_Time: totalSetMins,
                                T_Qua: 0,
                                status: 'infusion progress',
                            }
                            : null
                    );
                    if (deviceId) {
                        startHourlyFlowRateSchedule({
                            deviceId: String(deviceId),
                            hourlySteps: steps,
                            patientName: formData.patientName || '',
                            medicineName: formData.medicineName || '',
                            bedNo: treeViewSelectedValue || '',
                            volumeConsumed: cardDetail?.volume_consumed || 0,
                            accessToken,
                            apiKey: API_KEY,
                            flowRateApiUrl: FLOW_RATE_API,
                            pubFlowRateApiUrl: PUB_FLOW_RATE_WITH_HOUR_API,
                            onStageChange: (stepIdx, newRPM) => {
                                setCardDetail((prev) => (prev ? { ...prev, RPM: newRPM } : null));
                            },
                        });
                    }
                    break;
                }

                case 'time_duration':
                    setCardDetail((prevState) =>
                        prevState
                            ? {
                                ...prevState,
                                RPM: 0,
                                T_L_Time: Number(formData.timeDuration),
                                T_Qua: 0,
                                status: 'infusion progress',
                            }
                            : null
                    );
                    break;

                case 'drip_quantity':
                    if (formDataDrip.type === 'flow_rate') {
                        setCardDetail((prevState) =>
                            prevState
                                ? {
                                    ...prevState,
                                    RPM: Number(formDataDrip.flowRate),
                                    T_L_Time: 0,
                                    T_Qua: Number(formData.dripQuantity),
                                    status: 'infusion progress',
                                }
                                : null
                        );
                    } else if (formDataDrip.type === 'time_duration') {
                        setCardDetail((prevState) =>
                            prevState
                                ? {
                                    ...prevState,
                                    RPM: 0,
                                    T_L_Time: Number(
                                        formDataDrip.timeDuration
                                    ),
                                    T_Qua: Number(formData.dripQuantity),
                                    status: 'infusion progress',
                                }
                                : null
                        );
                    }
                    break;

                default:
                    // No type selected - patient/medicine names already updated
                    break;
            }

            await fetchAttributes().then(() => {
                setInfusionLoading(false);
                setIsInfusionModalOpen(false);
            });

            // Show success message only if type was selected
            if (formData.type) {
                // Determine which fields changed
                const originalPatientName = cardDetail?.patient_name || '';
                const originalMedicineName = cardDetail?.medicine_name || '';
                const patientNameChanged = formData.patientName !== originalPatientName;
                const medicineNameChanged = formData.medicineName !== originalMedicineName;

                let successMessage = 'Device updated successfully';
                if (patientNameChanged && medicineNameChanged) {
                    successMessage = 'Device updated successfully - Patient name and Medicine name updated';
                } else if (patientNameChanged) {
                    successMessage = 'Device updated successfully - Patient name updated';
                } else if (medicineNameChanged) {
                    successMessage = 'Device updated successfully - Medicine name updated';
                }

                MySwal.fire({
                    position: 'top-end',
                    icon: 'success',
                    title: successMessage,
                    showConfirmButton: false,
                    timer: 3000,
                    toast: true,
                    customClass: {
                        popup: 'text-green bg-light_green', // Add custom class to the popup
                    },
                    width: 'auto',
                });
            }
        } catch (error) {
            console.error('Error in API call:', error);
            setInfusionLoading(false);
        }
    };

    const handleStartStop = async (cardInfo: DeviceCardDetail) => {
        if (
            cardInfo.status == 'Infusion Not Started' ||
            cardInfo.status == 'Infusion Complete'
        ) {
            setFormData({
                type: '',
                dripQuantity: '',
                timeDuration: '',
                flowRate: ''
            });
            setFormDataDrip({
                type: '',
                timeDuration: '',
                flowRate: ''
            });
            setSelectedInfusionInput('');
            setSelectedInfusionInputDrip('');
            setIsInfusionModalOpen(true);
        } else {
            MySwal.fire({
                title: t('device_details_page.stop_confirmation_title'),
                text: t('device_details_page.stop_confirmation_message'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: 'rgba(3, 70, 162, 1)',
                cancelButtonColor: '#d33',
                confirmButtonText: t('device_details_page.confirm_stop'),
                cancelButtonText: t('device_details_page.cancel'),
            }).then(async (result) => {
                if (result.isConfirmed) {
                    setInfusionLoading(true);
                    let apiEndpoint = STOP_INFUSION_API;
                    let requestBody = {
                        device_id: cardInfo.device_id,
                        volume: cardInfo.volume_consumed,
                    };
                    let apiEndpoint_1 = PUB_STOP_INFUSION_API;
                    let requestBody_1 = {
                        device_id: cardInfo.device_id,
                    };
                    // Call the appropriate API
                    try {
                        if (apiEndpoint != '') {
                            const response = await fetch(apiEndpoint, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'x-api-key': API_KEY, // Replace with your actual API key
                                    'access-token': accessToken, // Use token if needed
                                },
                                body: JSON.stringify(requestBody),
                            });

                            const result = await response.json();

                            // if (response.ok) {
                            //     // console.log('Form submitted successfully:', result);
                            // } else {
                            //     // console.error('Error submitting form:', result.message);
                            // }
                        }
                        if (apiEndpoint_1 != '') {
                            const response_1 = await fetch(apiEndpoint_1, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'x-api-key': API_KEY, // Replace with your actual API key
                                    'access-token': accessToken, // Use token if needed
                                },
                                body: JSON.stringify(requestBody_1),
                            });

                            const result_1 = await response_1.json();

                            // if (response_1.ok) {
                            //     // console.log('Form submitted successfully:', result_1);
                            // } else {
                            //     // console.error('Error submitting form:', result_1.message);
                            // }
                        }

                        setCardDetail((prevState) =>
                            prevState
                                ? {
                                    ...prevState,
                                    RPM: 0,
                                    T_L_Time: 0,
                                    T_Qua: 0,
                                    status: 'infusion stopped',
                                }
                                : null
                        );

                        // Reset form data and form drip data
                        setFormData({
                            type: '',
                        });
                        setFormDataDrip({
                            type: '',
                        });
                        setSelectedInfusionInput('');
                        setSelectedInfusionInputDrip('');

                        await fetchAttributes().then(() => {
                            setInfusionLoading(false);
                        });

                        MySwal.fire({
                            position: 'top-end', // Position the toast in the top-right corner
                            icon: 'success',
                            title: 'Device Stopped successfully',
                            showConfirmButton: false,
                            timer: 3000,
                            toast: true,
                            customClass: {
                                popup: 'text-green bg-light_green', // Add custom class to the popup
                            },
                            width: 'auto',
                        });
                    } catch (error) {
                        console.error('Error in API call:', error);
                        setInfusionLoading(false);
                    }
                }
            });
        }
    };

    return (
        <div className="mx-auto xl:px-0 2xl:px-5 lg:px-5 2xl:max-w-full lg:max-w-[685px] xl:max-w-full">
            <div className="flex align-items-center justify-between 2xl:w-full xl:w-full lg:w-[700px]">
                {/* <div className="flex items-center"> */}
                <div className="flex items-center">
                    <div className="flex items-center cursor-pointer">
                        {/* <IoMdArrowRoundBack className="w-8 h-8" />{' '} */}
                        {userRole.toLowerCase() == HOSPITAL.toLowerCase() ? (
                            <h1 className="text-lg font-semibold  ml-2">
                                {t('device_details_page.title')}
                            </h1>
                        ) : (
                            <h1 className="text-lg font-semibold  ml-2">
                                {t('device_details_page.title_1')}
                            </h1>
                        )}
                    </div>
                    {/* {userRole.toLowerCase() == HOSPITAL.toLowerCase() && (
                        <>
                            <button
                                className="bg-primary rounded text-center py-1.5 px-4 text-white ml-4 relative "
                                onClick={handleAddDevice}
                            >
                                <span className="absolute left-0 ml-4">+</span>
                                <span className="ml-3">
                                    {t('device_details_page.add_device')}
                                </span>
                            </button>
                        </>
                    )} */}
                </div>
                {/* </div> */}
            </div>
            <div
                className="flex justify-content-between
                 align-items-center my-4"
            >
                <SearchBar
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                />
                {userRole.toLowerCase() != INDIVIDUAL.toLowerCase() && (
                    <div className="flex">
                        <Dropdown
                            show={isDropdownVisible}
                            onClick={(event) => handleDropdownToggle(event)}
                            data-click-type="btn-open"
                        >
                            <Dropdown.Toggle
                                as={CustomDropdownToggle}
                                variant="primary"
                                id="dropdown-basic"
                            >
                                {t('user_management.hospital_user.filter')}
                            </Dropdown.Toggle>
                            <Dropdown.Menu onClick={(e) => e.stopPropagation()}>
                                <span className="text-sm ml-4 font-bold text-black">
                                    {t('user_management.hospital_user.filter')}
                                </span>
                                <div className="absolute right-0 -top-0.5 cursor-pointer">
                                    {/* <button
                                        className="relative text-xs top-2.5 right-10 cursor-pointer"
                                        aria-label="btn-close"
                                        onClick={(event) => handleCloseModal(event)}
                                        data-click-type="btn-close"
                                    ></button> */}
                                    <span
                                        className="text-xs absolute top-3 right-10 cursor-pointer"
                                        onClick={(event) =>
                                            handleCloseModal(event)
                                        }
                                    >
                                        {t('device_details_page.close')}{' '}
                                        <IoClose className="absolute bottom-[0.1rem] ml-9 -mt-3" />
                                    </span>
                                </div>

                                <Dropdown.Header
                                    className="font-bold text-black text-sm flex justify-between items-center"
                                    onClick={toggleInfusionStatusExpand}
                                >
                                    {t(
                                        'device_details_page.select_infusion_status'
                                    )}
                                    {isInfusionStatusExpanded ? (
                                        <DownIcon />
                                    ) : (
                                        <UpIcon />
                                    )}
                                </Dropdown.Header>
                                {isInfusionStatusExpanded && (
                                    <>
                                        <Dropdown.Item
                                            className="flex items-center"
                                            onClick={() =>
                                                handleSortChange('all infusion')
                                            }
                                        >
                                            <input
                                                type="radio"
                                                name="all_infusion"
                                                value="all infusion"
                                                aria-label="all_infusion"
                                                checked={
                                                    sort === 'all infusion'
                                                }
                                                className="mr-2"
                                                readOnly
                                            />
                                            {t(
                                                'device_details_page.all_infusion'
                                            )}
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                            className="flex items-center"
                                            onClick={() =>
                                                handleSortChange(
                                                    'Infusion In Progress'
                                                )
                                            }
                                        >
                                            <input
                                                type="radio"
                                                name="infusion_progress"
                                                value="infusion progress"
                                                aria-label="infusion_progress"
                                                checked={
                                                    sort === 'Infusion In Progress'
                                                }
                                                className="mr-2"
                                                readOnly
                                            />
                                            {t(
                                                'device_details_page.infusion_progress'
                                            )}
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                            className="flex items-center"
                                            onClick={() =>
                                                handleSortChange(
                                                    'Infusion Not Started'
                                                )
                                            }
                                        >
                                            <input
                                                type="radio"
                                                name="infusion_stopped"
                                                value="Infusion Not Started"
                                                aria-label="infusion_stopped"
                                                checked={
                                                    sort === 'Infusion Not Started'
                                                }
                                                className="mr-2"
                                                readOnly
                                            />
                                            {t(
                                                'device_details_page.infusion_stopped'
                                            )}
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                            className="flex items-center"
                                            onClick={() =>
                                                handleSortChange(
                                                    'Infusion Complete'
                                                )
                                            }
                                        >
                                            <input
                                                type="radio"
                                                name="infusion_completed"
                                                value="Infusion Complete"
                                                aria-label="infusion_completed"
                                                checked={
                                                    sort ===
                                                    'Infusion Complete'
                                                }
                                                className="mr-2"
                                                readOnly
                                            />
                                            {t(
                                                'device_details_page.infusion_completed'
                                            )}
                                        </Dropdown.Item>
                                    </>
                                )}

                                <Dropdown.Header
                                    className="font-bold text-black text-sm flex justify-between items-center"
                                    onClick={toggleDeviceExpand}
                                >
                                    {t('device_details_page.select_device')}
                                    {isDeviceExpanded ? (
                                        <DownIcon />
                                    ) : (
                                        <UpIcon />
                                    )}
                                </Dropdown.Header>
                                {isDeviceExpanded && (
                                    <>
                                        <Dropdown.Item
                                            className="flex items-center"
                                            onClick={() =>
                                                handleFilterChange('all device')
                                            }
                                        >
                                            <input
                                                type="radio"
                                                name="all_device"
                                                value="all device"
                                                aria-label="all_device"
                                                checked={
                                                    filter === 'all device'
                                                }
                                                className="mr-2"
                                                readOnly
                                            />
                                            {t(
                                                'device_details_page.all_device'
                                            )}
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                            className="flex items-center"
                                            onClick={() =>
                                                handleFilterChange('active')
                                            }
                                        >
                                            <input
                                                type="radio"
                                                name="active_device"
                                                value="active"
                                                aria-label="active_device"
                                                checked={filter === 'active'}
                                                className="mr-2"
                                                readOnly
                                            />
                                            {t(
                                                'device_details_page.active_device'
                                            )}
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                            className="flex items-center"
                                            onClick={() =>
                                                handleFilterChange('inactive')
                                            }
                                        >
                                            <input
                                                type="radio"
                                                name="inactive_device"
                                                value="inactive"
                                                aria-label="inactive_device"
                                                checked={filter === 'inactive'}
                                                className="mr-2"
                                                readOnly
                                            />
                                            {t(
                                                'device_details_page.in_active_device'
                                            )}
                                        </Dropdown.Item>
                                    </>
                                )}

                                <div className="flex cursor-pointer align-items-center justify-content-center space-x-4 px-4 py-2">
                                    <button
                                        className="bg-white border-2 border-primary  text-primary px-4 py-1 rounded"
                                        onClick={(event) => handleClear(event)}
                                        data-click-type="btn-clear"
                                    >
                                        {t('management.device.clear')}
                                    </button>
                                    <button
                                        className="text-white bg-primary rounded px-4 py-1"
                                        onClick={(event) => handleSave(event)}
                                        data-click-type="btn-save"
                                    >
                                        {t('management.device.apply')}
                                    </button>
                                </div>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                )}
            </div>
            {/* <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-4 mt-5 w-[97%]"> */}
            {paginatedUsers != null && paginatedUsers?.length != 0 ? (
                <>
                    <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-6 mt-5 w-[97%]">
                        {paginatedUsers.map((device, index) => (
                            <div
                                key={device.id}
                                className="rounded-2xl bg-white p-4 cursor-pointer transition hover:-translate-y-1 hover:shadow-md shadow-sm"
                                style={{
                                    border: device.error_code && device.error_code !== ''
                                        ? '2px solid #ef4444'
                                        : device.deviceStatus?.toLowerCase() === 'active'
                                        ? '2px solid #22c55e'
                                        : '1px solid #e5e7eb'
                                }}
                                onClick={() => handleCartClick(device)}
                            >
                                {/* Image + info row */}
                                <div className="grid gap-4" style={{ gridTemplateColumns: '100px 1fr' }}>

                                    {/* Left: Device image */}
                                    <div className="flex flex-col items-center justify-center shrink-0">
                                        <img
                                            src={DRIPSET_DEVICE_SRC}
                                            alt="DripSet Device"
                                            className="h-[150px] w-auto object-contain"
                                            style={{ filter: 'drop-shadow(0 8px 16px rgba(15,23,42,0.12))' }}
                                        />
                                    </div>

                                    {/* Right: Info */}
                                    <div className="flex flex-col gap-4 pt-2">

                                        {/* Patient Name | Medicine Name — 2-column top row */}
                                        <div className="grid grid-cols-2 gap-x-2">

                                            {/* Patient Name */}
                                            <div className="flex items-start gap-2">
                                                <User className="h-4 w-4 shrink-0 text-blue-500 mt-0.5" />
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-[10px] font-medium text-slate-500 leading-tight">
                                                        {t('device_details_page.patient_name')}
                                                    </p>
                                                    <p className="text-[11px] font-bold text-slate-900 truncate mt-0.5">
                                                        {device.patient_name || '--'}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Medicine Name */}
                                            <div className="flex items-start gap-2">
                                                <Pill className="h-4 w-4 shrink-0 text-pink-500 mt-0.5" />
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-[10px] font-medium text-slate-500 leading-tight">
                                                        {t('device_details_page.medicine_name')}
                                                    </p>
                                                    <p className="text-[11px] font-bold text-slate-900 truncate mt-0.5">
                                                        {device.medicine_name || '--'}
                                                    </p>
                                                </div>
                                            </div>

                                        </div>

                                        {/* 2×2 grid */}
                                        <div className="grid grid-cols-2 gap-y-4 gap-x-2">

                                            {/* Device ID */}
                                            <div className="flex items-start gap-2">
                                                <SquareDashedMousePointer className="h-4 w-4 shrink-0 text-violet-500 mt-0.5" />
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-[10px] font-medium text-slate-500 leading-tight">
                                                        {t('device_details_page.device_id')}
                                                    </p>
                                                    <p className="text-[11px] font-bold text-slate-900 truncate mt-0.5" title={device.device_id}>
                                                        {device.device_id || '--'}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Infusion Status */}
                                            <div className="flex items-start gap-2">
                                                <Play className="h-4 w-4 shrink-0 text-green-500 mt-0.5" />
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-[10px] font-medium text-slate-500 leading-tight">
                                                        {t('device_details_page.infusion_status')}
                                                    </p>
                                                    <p
                                                        className="text-[11px] font-bold truncate mt-0.5"
                                                        style={{
                                                            color: device.error_code && device.error_code !== ''
                                                                ? '#ef4444'
                                                                : device.status === 'Infusion In Progress' || device.status === 'Infusion Started'
                                                                ? '#16a34a'
                                                                : device.status === 'Infusion Complete' || device.status === 'Infusion Completed'
                                                                ? '#2563eb'
                                                                : '#0f172a'
                                                        }}
                                                    >
                                                        {device.error_code && device.error_code !== ''
                                                            ? device.error_code
                                                            : device.status === 'Infusion Not Started'
                                                                ? 'Not Started'
                                                                : device.status === 'Infusion In Progress' || device.status === 'Infusion Started'
                                                                    ? 'In Progress'
                                                                    : device.status === 'Infusion Complete' || device.status === 'Infusion Completed'
                                                                        ? 'Complete'
                                                                        : '--'}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Device Status */}
                                            <div className="flex items-start gap-2">
                                                <CircleAlert className="h-4 w-4 shrink-0 text-red-500 mt-0.5" />
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-[10px] font-medium text-slate-500 leading-tight">
                                                        {t('device_details_page.device_status')}
                                                    </p>
                                                    <p
                                                        className="text-[11px] font-bold truncate mt-0.5"
                                                        style={{ color: device.deviceStatus?.toLowerCase() === 'active' ? '#16a34a' : '#0f172a' }}
                                                    >
                                                        {device.deviceStatus || '--'}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Battery Capacity */}
                                            <div className="flex items-start gap-2">
                                                <BatteryCharging className="h-4 w-4 shrink-0 text-orange-500 mt-0.5" />
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-[10px] font-medium text-slate-500 leading-tight">
                                                        {t('device_details_page.battery_level')}
                                                    </p>
                                                    <p className="text-[11px] font-bold text-slate-900 truncate mt-0.5 inline-flex items-center gap-1">
                                                        {device.battery_percentage} %
                                                        {device.deviceStatus?.toLowerCase() === 'active' && String(device.battery_status) === '1' && (
                                                            <FiZap className="text-yellow-500" size={11} />
                                                        )}
                                                    </p>
                                                </div>
                                            </div>


                                        </div>
                                    </div>
                                </div>

                                {/* ── Bottom StatPill row ── */}
                                <div className="border-t border-slate-100 pt-3 mt-4">
                                    <div className="grid grid-cols-3 gap-2">

                                        {/* Infusion Rate */}
                                        <div className="flex items-center gap-2">
                                            <Droplets className="h-[18px] w-[18px] shrink-0 text-blue-500" />
                                            <div className="min-w-0 flex-1">
                                                <div className="text-[10px] font-medium text-slate-500 leading-tight">
                                                    {t('device_details_page.infusion_rate')}
                                                </div>
                                                <div className="mt-0.5 text-xs font-bold text-slate-900 leading-tight">
                                                    {device.infusion_rate} ml/min
                                                </div>
                                            </div>
                                        </div>

                                        {/* VTBI */}
                                        <div className="flex items-center gap-2">
                                            <Gauge className="h-[18px] w-[18px] shrink-0 text-green-500" />
                                            <div className="min-w-0 flex-1">
                                                <div className="text-[10px] font-medium text-slate-500 leading-tight">
                                                    {t('device_details_page.volume')}
                                                </div>
                                                <div className="mt-0.5 text-xs font-bold text-slate-900 leading-tight">
                                                    {device.volume} ml
                                                </div>
                                            </div>
                                        </div>

                                        {/* TTBI */}
                                        <div className="flex items-center gap-2">
                                            <Clock3 className="h-[18px] w-[18px] shrink-0 text-violet-500" />
                                            <div className="min-w-0 flex-1">
                                                <div className="text-[10px] font-medium text-slate-500 leading-tight">
                                                    {t('device_details_page.time_duration')}
                                                </div>
                                                <div className="mt-0.5 text-xs font-bold text-slate-900 leading-tight">
                                                    {device.time_duration} mins
                                                </div>
                                            </div>
                                        </div>

                                    </div>
                                </div>

                            </div>
                        ))}
                    </div>
                </>
            ) : (
                <>
                    <div className="text-center">{t('device_details_page.no_data')}</div>
                </>
            )}
            {paginatedUsers.length > 0 && sortedData.length > 10 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    rowsPerPage={rowsPerPage}
                    setRowsPerPage={setRowsPerPage}
                    handlePageChange={setCurrentPage}
                    disablePrevious={!hasPaginatedUsers}
                    disableNext={!hasPaginatedUsers}
                />
            )}
            {/* </div> */}

            {isFirstModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center z-50 bg-gray-800 bg-opacity-75">
                    <div className="bg-white p-6 rounded shadow-lg 2xl:w-1/5  xl:w-1/4 lg:[30%] text-center relative">
                        <div className="my-2">
                            <button
                                className="absolute top-2 right-2 text-black hover:text-gray-700"
                                onClick={handleCloseFirstModal}
                                aria-label="close"
                            >
                                <CloseIcon />
                            </button>
                        </div>
                        <h2 className="text-xl font-semibold mb-2">
                            {t('device_details_page.popup_title')}
                            <br /> {t('device_details_page.popup_title1')}
                        </h2>
                        <p className="text-xs text center">
                            {t('device_details_page.discription')} <br />{' '}
                            {t('device_details_page.discription1')}
                        </p>
                        <div className="mt-2 items-center justify-content-center flex">
                            <button
                                className="bg-primary text-white py-2 px-4 mx-2"
                                onClick={handleOpenViewInfusionModal}
                            >
                                {t('device_details_page.view')}
                            </button>
                            <button
                                className="bg-white text-primary py-2 px-3 mx-2 border-2 border-primary"
                                onClick={handleTakeOverConfirmationModal}
                            >
                                {t('device_details_page.take_over')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {isTakeOverModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center z-50 bg-gray-800 bg-opacity-75">
                    <div className="bg-white p-6 rounded shadow-lg 2xl:w-1/5  xl:w-1/4 lg:[30%] text-center relative">
                        <div className="my-2">
                            <button
                                className="absolute top-2 right-2 text-black hover:text-gray-700"
                                onClick={handleCloseTakeOverModal}
                                aria-label="close"
                            >
                                <CloseIcon />
                            </button>
                        </div>
                        <h2 className="text-lg font-semibold mb-2">
                            {t('device_details_page.popup_title_1')}
                            <br /> {t('device_details_page.popup_title_2')}
                        </h2>
                        <p className="text-sm  font-normal text-center">
                            {t('device_details_page.discription_1')} <br />{' '}
                            {t('device_details_page.discription_2')}
                        </p>
                        <div className="mt-2 items-center justify-content-center flex">
                            <button
                                className="bg-primary text-white py-1 px-2 mx-2"
                                onClick={handleOpenInfusionModal}
                            >
                                {t('device_details_page.go_to_infusion')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isInfusionDetailModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/60 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl w-[500px] max-w-[95vw] relative overflow-hidden">

                        {/* Close button */}
                        <button
                            className="absolute top-3 right-3 z-10 flex items-center justify-center h-7 w-7 rounded-full bg-slate-900 text-white hover:bg-slate-700 transition"
                            aria-label="close"
                            onClick={handleCloseSecondModal}
                        >
                            <IoClose size={16} />
                        </button>

                        {/* ── Top info section ── */}
                        <div className="p-4 pb-2">
                            <div className="grid gap-3" style={{ gridTemplateColumns: '120px 1fr' }}>

                                {/* Device image */}
                                <div className="flex items-center justify-start">
                                    <img
                                        src={DRIPSET_DEVICE_SRC}
                                        alt="DripSet Device"
                                        className="h-[180px] w-auto object-contain -ml-2"
                                        style={{ filter: 'drop-shadow(0 8px 16px rgba(15,23,42,0.15))' }}
                                    />
                                </div>

                                {/* Info rows */}
                                <div className="flex flex-col gap-3 justify-center">

                                    {/* Patient Name | Medicine Name — parallel */}
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="flex items-start gap-1.5">
                                            <User className="h-3.5 w-3.5 shrink-0 text-blue-500 mt-0.5" />
                                            <div className="min-w-0">
                                                <p className="text-[10px] font-medium text-slate-400 leading-none">{t('device_details_page.patient_name')}</p>
                                                <p className="text-xs font-bold text-slate-900 mt-0.5 truncate">{cardDetail?.patient_name || '--'}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-1.5 min-w-0">
                                            <Pill className="h-3.5 w-3.5 shrink-0 text-pink-500 mt-0.5" />
                                            <div className="min-w-0 flex-1 overflow-hidden">
                                                <p className="text-[10px] font-medium text-slate-400 leading-none">{t('device_details_page.medicine_name')}</p>
                                                <p className="text-xs font-bold text-slate-900 mt-0.5 whitespace-nowrap overflow-hidden text-ellipsis">{cardDetail?.medicine_name || '--'}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Device ID | Device Name — parallel */}
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="flex items-start gap-1.5">
                                            <SquareDashedMousePointer className="h-3.5 w-3.5 shrink-0 text-violet-500 mt-0.5" />
                                            <div className="min-w-0 flex-1">
                                                <p className="text-[10px] font-medium text-slate-400 leading-none">{t('device_details_page.device_id')}</p>
                                                <p className="text-xs font-bold text-slate-900 mt-0.5 break-all">{cardDetail?.device_id || '--'}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-1.5">
                                            <Cpu className="h-3.5 w-3.5 shrink-0 text-teal-500 mt-0.5" />
                                            <div className="min-w-0 flex-1">
                                                <p className="text-[10px] font-medium text-slate-400 leading-none">{t('device_details_page.device_name')}</p>
                                                <p className="text-xs font-bold text-slate-900 mt-0.5 truncate">{cardDetail?.device_name || '--'}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Assigned | Battery — parallel */}
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="flex items-start gap-1.5">
                                            <FaUserNurse className="h-3.5 w-3.5 shrink-0 text-blue-400 mt-0.5" />
                                            <div className="min-w-0">
                                                <p className="text-[10px] font-medium text-slate-400 leading-none">{t('device_details_page.assigned')}</p>
                                                <p className="text-xs font-bold text-slate-900 mt-0.5 truncate">{cardDetail?.assigned || '--'}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-1.5">
                                            <BatteryCharging className="h-3.5 w-3.5 shrink-0 text-orange-500 mt-0.5" />
                                            <div className="min-w-0">
                                                <p className="text-[10px] font-medium text-slate-400 leading-none">{t('device_details_page.battery_level')}</p>
                                                <p className="text-xs font-bold text-slate-900 mt-0.5 inline-flex items-center gap-1">
                                                    {cardDetail?.battery_percentage} %
                                                    {cardDetail?.deviceStatus?.toLowerCase() === 'active' && String(cardDetail?.battery_status) === '1' && (
                                                        <FiZap className="text-yellow-500" size={11} />
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Device Status | Infusion Status — parallel */}
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="flex items-start gap-1.5">
                                            <CircleAlert className="h-3.5 w-3.5 shrink-0 text-red-500 mt-0.5" />
                                            <div className="min-w-0">
                                                <p className="text-[10px] font-medium text-slate-400 leading-none">{t('device_details_page.device_status')}</p>
                                                <p
                                                    className="text-xs font-bold mt-0.5"
                                                    style={{ color: cardDetail?.deviceStatus?.toLowerCase() === 'active' ? '#16a34a' : '#0f172a' }}
                                                >
                                                    {cardDetail?.deviceStatus || '--'}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-start gap-1.5">
                                            <Play className="h-3.5 w-3.5 shrink-0 text-green-500 mt-0.5" />
                                            <div className="min-w-0">
                                                <p className="text-[10px] font-medium text-slate-400 leading-none">{t('device_details_page.infusion_status')}</p>
                                                <p
                                                    className="text-xs font-bold mt-0.5"
                                                    style={{
                                                        color: cardDetail?.error_code && cardDetail.error_code !== ''
                                                            ? '#ef4444'
                                                            : cardDetail?.status === 'Infusion In Progress' || cardDetail?.status === 'Infusion Started'
                                                            ? '#16a34a'
                                                            : cardDetail?.status === 'Infusion Complete' || cardDetail?.status === 'Infusion Completed'
                                                            ? '#2563eb'
                                                            : '#0f172a'
                                                    }}
                                                >
                                                    {cardDetail?.error_code && cardDetail.error_code !== '' ? cardDetail.error_code
                                                        : cardDetail?.status === 'Infusion Not Started' ? 'Not Started'
                                                            : cardDetail?.status === 'Infusion In Progress' || cardDetail?.status === 'Infusion Started' ? 'In Progress'
                                                                : cardDetail?.status === 'Infusion Complete' || cardDetail?.status === 'Infusion Completed' ? 'Complete'
                                                                    : '--'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                </div>

                            </div>
                        </div>

                        {/* ── Infusion Settings panel ── */}
                        <div className="mx-4 mb-3 rounded-xl bg-blue-50 overflow-hidden">
                            <div className="flex items-center justify-between px-3.5 py-2 bg-blue-100">
                                <h3 className="text-xs font-bold text-blue-700">{t('device_details_page.infusion_settings')}</h3>
                                {cardDetail &&
                                    (cardDetail.status === 'Infusion In Progress' || cardDetail.status === 'Infusion Started') &&
                                    isInfusionButtonVisible && !cardDetail.error_code && (
                                        <button
                                            className="flex items-center gap-1 text-[11px] font-semibold text-blue-600 hover:text-blue-800 transition"
                                            onClick={() => handleStartInfusionModal(cardDetail)}
                                        >
                                            <MdOutlineModeEdit size={13} />
                                            {t('management.device.edit')}
                                        </button>
                                    )}
                            </div>
                            <div className="px-3.5 py-2 flex flex-col gap-2">

                                {/* Total Volume */}
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-1.5">
                                        <Gauge className="h-3.5 w-3.5 text-slate-500" />
                                        <span className="text-[11px] font-medium text-slate-600">{t('device_details_page.total_volume')}</span>
                                    </div>
                                    <span className="text-xs font-bold text-slate-900">{cardDetail?.Sel_Qua ?? 0} ml</span>
                                </div>

                                {/* Infusion Rate */}
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-1.5">
                                        <Droplets className="h-3.5 w-3.5 text-blue-500" />
                                        <span className="text-[11px] font-medium text-slate-600">{t('device_details_page.infusion_rate')}</span>
                                    </div>
                                    <span className="text-xs font-bold text-slate-900">{cardDetail?.infusion_rate ?? '--'} ml/min</span>
                                </div>

                                {/* Time to be infused */}
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-1.5">
                                        <Clock3 className="h-3.5 w-3.5 text-violet-500" />
                                        <span className="text-[11px] font-medium text-slate-600">{t('device_details_page.time_infused')}</span>
                                    </div>
                                    <span className="text-xs font-bold text-slate-900">{cardDetail?.time_duration ?? '--'} mins</span>
                                </div>

                                {/* Volume to be infused */}
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-1.5">
                                        <Gauge className="h-3.5 w-3.5 text-green-500" />
                                        <span className="text-[11px] font-medium text-slate-600">{t('device_details_page.volume_infused')}</span>
                                    </div>
                                    <span className="text-xs font-bold text-slate-900">{cardDetail?.volume ?? 0} ml</span>
                                </div>

                            </div>
                        </div>

                        {/* ── Nurse Error Summary Section (Image 2 mockup) ── */}
                        {userRole.toLowerCase() === NURSE.toLowerCase() && cardDetail?.error_code && cardDetail.error_code !== '' && cardDetail.error_code !== '--' && (
                            <div className="mx-4 mb-3 p-3.5 rounded-xl border border-slate-200 bg-white shadow-sm">
                                <h4 className="text-xs font-bold text-slate-900 mb-1.5">Error Summary</h4>
                                <div className="relative">
                                    <textarea
                                        className="w-full p-2.5 pr-14 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none bg-slate-50/50 text-slate-800 placeholder-slate-400"
                                        rows={2.5}
                                        maxLength={250}
                                        placeholder="Enter error summary..."
                                        value={errorSummaryText}
                                        onChange={(e) => setErrorSummaryText(e.target.value)}
                                    />
                                    <span className="absolute bottom-2 right-2.5 text-[10px] font-medium text-slate-400">
                                        {errorSummaryText.length} / 250
                                    </span>
                                </div>
                                <div className="flex items-center justify-between mt-2 gap-2">
                                    <span className="text-[11px] text-slate-500 leading-tight">
                                        Provide brief details about the device issue or error.
                                    </span>
                                    <button
                                        type="button"
                                        className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-full flex items-center gap-1.5 transition shadow-md shadow-blue-600/25 disabled:opacity-50 shrink-0"
                                        onClick={handleNurseSubmitErrorSummary}
                                        disabled={!errorSummaryText.trim() || isSubmittingErrorSummary}
                                    >
                                        {isSubmittingErrorSummary ? 'Submitting...' : 'Submit'} <IoSend size={13} />
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* ── Action button ── */}
                        {isInfusionButtonVisible && cardDetail && (
                            cardDetail.error_code === '' ? (
                                <div className="px-5 pb-5">
                                    <button
                                        className="w-full py-3 rounded-xl font-bold text-sm text-white bg-blue-600 hover:bg-blue-700 active:scale-[0.98] transition shadow-lg shadow-blue-600/25"
                                        onClick={() => handleStartStop(cardDetail)}
                                    >
                                        {cardDetail.status === 'Infusion Not Started' ||
                                            cardDetail.status === 'Infusion Complete' ||
                                            cardDetail.status === 'Infusion Completed'
                                            ? t('device_details_page.start_infusion')
                                            : t('device_details_page.stop_infusion')}
                                    </button>
                                </div>
                            ) : (
                                <div className="px-5 pb-5">
                                    <div className="w-full py-3 rounded-xl font-bold text-sm text-white bg-red-500 text-center">
                                        {t('device_details_page.stopped_infusion')}
                                    </div>
                                </div>
                            )
                        )}

                    </div>
                </div>
            )}


            {isInfusionModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center z-50 bg-gray-800 bg-opacity-75">
                    {userRole.toLowerCase() != INDIVIDUAL.toLowerCase() ? (
                        <div className="bg-white p-4 rounded shadow-lg w-[719px] relative">
                            <button
                                className="absolute top-2 right-2 text-black hover:text-gray-700"
                                aria-label="close"
                                onClick={handleCloseInfusionModal}
                            >
                                <CloseIcon />
                            </button>
                            <form onSubmit={handleSubmit}>
                                <input
                                    type="hidden"
                                    name="device_id"
                                    value={cardDetail?.device_id} // This is where you pass the hidden value
                                />
                                <div className="w-full flex flex-col ">
                                    <h3 className="font-bold text-primary mb-2">
                                        {t(
                                            'device_details_page.patient_information'
                                        )}
                                    </h3>
                                    <div className="flex mb-3">
                                        <div className="flex flex-col w-1/2 pr-2">
                                            <label className=" text-sm text-black">
                                                {t(
                                                    'device_details_page.patient_name'
                                                )}
                                            </label>
                                            <input
                                                id="patient_name"
                                                type="text"
                                                className="form-control px-3 py-2 relative "
                                                value={formData.patientName}
                                                onChange={(e) =>
                                                    setFormData(
                                                        (prevFormData) => ({
                                                            ...prevFormData,
                                                            patientName:
                                                                e.target.value,
                                                        })
                                                    )
                                                }
                                            />
                                            <label className=" text-sm text-black">
                                                {t(
                                                    'device_details_page.medicine_name'
                                                )}
                                            </label>
                                            <input
                                                id="medicine_name"
                                                type="text"
                                                className="form-control px-3 py-2 relative "
                                                value={formData.medicineName}
                                                onChange={(e) =>
                                                    setFormData(
                                                        (prevFormData) => ({
                                                            ...prevFormData,
                                                            medicineName:
                                                                e.target.value,
                                                        })
                                                    )
                                                }
                                            />
                                        </div>
                                        <div className="tree-container">
                                            <h4>{t('tree_view.title')}</h4>
                                            <div className="checkbox-container">
                                                {treeViewData && treeViewData.length > 0 ? (
                                                    <MyTreeView
                                                        data={treeViewData}
                                                        isDisabled={true}
                                                    />
                                                ) : (
                                                    <span className="text-sm text-gray-500">No hierarchy data</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <h3 className="font-bold text-primary mb-2">
                                        {t(
                                            'device_details_page.infusion_settings'
                                        )}
                                    </h3>
                                    <div className="flex mb-3">
                                        <div className="flex flex-col w-1/2">
                                            <label
                                                htmlFor="volume"
                                                className="text-sm text-black"
                                                aria-label="volume"
                                            >
                                                {t('device_details_page.volume_infused')}
                                            </label>
                                            <input
                                                id="volume"
                                                type="text"
                                                className="form-control px-3 py-2 relative "
                                                value={
                                                    cardDetail
                                                        ? cardDetail.volume
                                                        : '--'
                                                }
                                                readOnly
                                                disabled
                                            />
                                            <span className="flex items-center ml-2 absolute left-[19.5rem] top-[15.7rem]">
                                                ml
                                            </span>
                                            {/* <span className="">
                                                {cardDetail
                                                    ? cardDetail.volume
                                                    : '--'}{' '}
                                                ml
                                            </span> */}
                                        </div>
                                    </div>
                                    <div className="flex mb-2">
                                        <div className="flex flex-col w-1/2">
                                            <label className="text-sm text-black">
                                                {t(
                                                    'device_details_page.infusion_select'
                                                )}
                                            </label>
                                            <select
                                                className="border rounded px-3 py-2.5"
                                                name="type"
                                                aria-label="type"
                                                value={formData.type}
                                                onChange={handleSelectChange}
                                            >
                                                <option value="">
                                                    {t(
                                                        'device_details_page.select'
                                                    )}
                                                </option>
                                                <option value="time_duration">
                                                    {t(
                                                        'device_details_page.time_infused'
                                                    )}
                                                </option>
                                                <option value="flow_rate">
                                                    {t(
                                                        'device_details_page.flow_rate'
                                                    )}
                                                </option>
                                                <option value="drip_quantity">
                                                    {t(
                                                        'device_details_page.drip_quantity'
                                                    )}
                                                </option>
                                                <option value="hourly_flow_rate">
                                                    {t(
                                                        'device_details_page.hourly_flow_rate'
                                                    )}
                                                </option>
                                            </select>
                                        </div>
                                        {selectedInfusionInput ===
                                            'hourly_flow_rate' && (
                                                <div className="flex flex-col mb-3 w-1/2 pl-2 bg-slate-50 p-2.5 rounded border border-slate-200">
                                                    <div className="flex justify-between items-center mb-2">
                                                        <span className="text-xs font-semibold text-slate-700">
                                                            Set Schedule Settings
                                                        </span>
                                                        <button
                                                            type="button"
                                                            onClick={handleAddHourlyStep}
                                                            className="text-[11px] bg-primary text-white px-2 py-0.5 rounded hover:bg-opacity-90 font-medium"
                                                        >
                                                            {t('device_details_page.add_hour')}
                                                        </button>
                                                    </div>
                                                    <div className="max-h-48 overflow-y-auto space-y-2 pr-1">
                                                        {(formData.hourlySteps || [{ hour: 1, setMins: 60, flowRate: 2.0 }]).map((step, index) => {
                                                            const stepVol = (Number(step.setMins || 0) * Number(step.flowRate || 0)).toFixed(1);
                                                            return (
                                                                <div key={index} className="flex flex-col gap-1 bg-white p-1.5 rounded border border-slate-200 text-xs">
                                                                    <div className="flex items-center justify-between font-semibold text-slate-700 text-[11px]">
                                                                        <span>{t('device_details_page.hour')} {step.hour}:</span>
                                                                        <span className="text-slate-500 font-normal text-[10px]">
                                                                            {t('device_details_page.set_volume')}: <strong className="text-slate-800">{stepVol} ml</strong>
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex items-center gap-1.5">
                                                                        <div className="flex flex-col flex-1 min-w-0">
                                                                            <label className="text-[10px] text-gray-500">{t('device_details_page.duration_mins')}</label>
                                                                            <input
                                                                                type="number"
                                                                                className="border rounded px-1.5 py-0.5 w-full text-xs"
                                                                                value={step.setMins}
                                                                                min={1}
                                                                                onChange={(e) => handleHourlyStepChange(index, 'setMins', Math.max(1, Number(e.target.value)))}
                                                                            />
                                                                        </div>
                                                                        <div className="flex flex-col flex-1 min-w-0">
                                                                            <label className="text-[10px] text-gray-500">{t('device_details_page.flow_rate')} (ml/min)</label>
                                                                            <input
                                                                                type="number"
                                                                                className="border rounded px-1.5 py-0.5 w-full text-xs"
                                                                                value={step.flowRate}
                                                                                min={0.5}
                                                                                max={7.0}
                                                                                step={0.5}
                                                                                onChange={(e) => handleHourlyStepChange(index, 'flowRate', Number(e.target.value))}
                                                                            />
                                                                        </div>
                                                                        {(formData.hourlySteps || []).length > 1 && (
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleRemoveHourlyStep(index)}
                                                                                className="text-red-500 hover:text-red-700 font-bold px-1 py-0.5 text-xs shrink-0 self-end mb-0.5"
                                                                                title="Remove Step"
                                                                            >
                                                                                ✕
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                    {(() => {
                                                        const steps = formData.hourlySteps || [{ hour: 1, setMins: 60, flowRate: 2.0 }];
                                                        const totalMins = steps.reduce((acc, curr) => acc + Number(curr.setMins || 0), 0);
                                                        const totalProgVol = steps.reduce((acc, curr) => acc + Number(curr.setMins || 0) * Number(curr.flowRate || 0), 0);
                                                        const vtbi = Number(cardDetail?.Sel_Qua || cardDetail?.volume || 0);
                                                        const remVol = vtbi > 0 ? Math.max(0, vtbi - totalProgVol) : 0;
                                                        return (
                                                            <div className="mt-2 flex flex-col gap-1 text-[11px] font-medium text-slate-600 bg-white p-1.5 rounded border border-slate-200">
                                                                <div className="flex justify-between">
                                                                    <span>{t('device_details_page.total_set_mins')}:</span>
                                                                    <span className="text-slate-900 font-bold">{totalMins} mins</span>
                                                                </div>
                                                                <div className="flex justify-between">
                                                                    <span>{t('device_details_page.programmed_volume')}:</span>
                                                                    <span className="text-primary font-bold">{totalProgVol.toFixed(1)} ml</span>
                                                                </div>
                                                                {vtbi > 0 && (
                                                                    <div className="flex justify-between border-t pt-1">
                                                                        <span>{t('device_details_page.remaining_volume')}:</span>
                                                                        <span className={`font-bold ${remVol < 0 ? 'text-red-500' : 'text-green-600'}`}>
                                                                            {remVol.toFixed(1)} / {vtbi} ml
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            )}
                                        {selectedInfusionInput ===
                                            'time_duration' && (
                                                <div className="flex flex-col mb-3 w-1/2 pl-2">
                                                    <label
                                                        htmlFor="duration"
                                                        className="text-sm text-black"
                                                    >
                                                        {t(
                                                            'device_details_page.time_infused'
                                                        )} <small>value between ({cardDetail?.Sel_Qua ? Math.round(cardDetail.Sel_Qua / 7) : 0} - {cardDetail?.Sel_Qua ? Math.round(cardDetail.Sel_Qua / 0.5) : 0})</small>
                                                    </label>
                                                    <div className="relative">
                                                        <input
                                                            id="duration"
                                                            type="number"
                                                            className="border rounded px-3 py-2 pl-8 w-full"
                                                            name="timeDuration"
                                                            min={cardDetail?.Sel_Qua ? Math.round(cardDetail.Sel_Qua / 7) : 0}
                                                            max={cardDetail?.Sel_Qua ? Math.round(cardDetail.Sel_Qua / 0.5) : 0}
                                                            value={
                                                                formData.timeDuration
                                                            }
                                                            onChange={
                                                                handleSelectChange
                                                            }
                                                            onKeyDown={(event) => {
                                                                const invalidKeys = ['e', 'E', '+', '-'];
                                                                if (invalidKeys.includes(event.key)) {
                                                                    event.preventDefault();
                                                                }
                                                            }}
                                                        />
                                                        <span className="absolute right-2 top-1/2 transform -translate-y-1/2">
                                                            min
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        {selectedInfusionInput ===
                                            'flow_rate' && (
                                                <div className="flex flex-col mb-3 w-1/2 pl-2">
                                                    <label
                                                        htmlFor="flow_rate"
                                                        className="text-sm text-black"
                                                    >
                                                        {t(
                                                            'device_details_page.flow_rate'
                                                        )} <small>value between (0.5 - 7.0)</small>
                                                    </label>
                                                    <div className="relative">
                                                        <input
                                                            id="flow_rate"
                                                            type="number"
                                                            className="border rounded px-3 py-2 pl-8 w-full"
                                                            name="flowRate"
                                                            value={
                                                                formData.flowRate
                                                            }
                                                            min={0.5}
                                                            max={7}
                                                            step={0.5}
                                                            onChange={
                                                                handleSelectChange
                                                            }
                                                            onKeyDown={(event) => {
                                                                const invalidKeys = ['e', 'E', '+', '-'];
                                                                if (invalidKeys.includes(event.key)) {
                                                                    event.preventDefault();
                                                                }
                                                            }}
                                                        />
                                                        <span className="absolute right-2 top-1/2 transform -translate-y-1/2">
                                                            ml/min
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        {selectedInfusionInput ===
                                            'drip_quantity' && (
                                                <div className="flex flex-col w-1/2 pl-2">
                                                    <label
                                                        htmlFor="drip_quantity"
                                                        className="text-sm text-black"
                                                    >
                                                        {t(
                                                            'device_details_page.drip_quantity'
                                                        )} <small>value between (60 - {cardDetail?.Sel_Qua || cardDetail?.volume || 0})</small>
                                                    </label>
                                                    <div className="relative">
                                                        <input
                                                            id="drip_quantity"
                                                            type="number"
                                                            className="border rounded px-3 py-2 pl-8 w-full"
                                                            name="dripQuantity"
                                                            min={60}
                                                            max={cardDetail?.Sel_Qua || cardDetail?.volume || 0}
                                                            value={
                                                                formData.dripQuantity
                                                            }
                                                            onChange={
                                                                handleInputChange
                                                            }
                                                            onKeyDown={(event) => {
                                                                const invalidKeys = ['e', 'E', '+', '-'];
                                                                if (invalidKeys.includes(event.key)) {
                                                                    event.preventDefault();
                                                                }
                                                            }}
                                                        />
                                                        <span className="absolute right-2 top-1/2 transform -translate-y-1/2">
                                                            gtt/ml
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                    </div>
                                    <div className="flex mb-2">
                                        {selectedInfusionInput ===
                                            'drip_quantity' && (
                                                <>
                                                    <div className="flex flex-col w-1/2">
                                                        <label className="text-sm text-black">
                                                            {t(
                                                                'device_details_page.select_drip'
                                                            )}
                                                        </label>
                                                        <select
                                                            aria-label="select"
                                                            className="border rounded px-3 py-2.5"
                                                            name="type"
                                                            value={
                                                                formDataDrip.type
                                                            }
                                                            onChange={
                                                                handleSelectChangeDrip
                                                            }
                                                        >
                                                            <option value="">
                                                                {t('device_details_page.select')}
                                                            </option>
                                                            <option value="time_duration">
                                                                {t(
                                                                    'device_details_page.time_infused'
                                                                )}
                                                            </option>
                                                            <option value="flow_rate">
                                                                {t(
                                                                    'device_details_page.flow_rate'
                                                                )}
                                                            </option>
                                                        </select>
                                                    </div>
                                                </>
                                            )}
                                        {selectedInfusionInputDrip ===
                                            'time_duration' && (
                                                <div className="flex flex-col mb-3 w-1/2 pl-2">
                                                    <label
                                                        htmlFor="duration"
                                                        className="text-sm text-black"
                                                    >
                                                        {t(
                                                            'device_details_page.time_infused'
                                                        )}{' '}
                                                        <small>
                                                            value between (
                                                            {formData.dripQuantity
                                                                ? Math.round(Number(formData.dripQuantity) / 7)
                                                                : 0}
                                                            {' - '}
                                                            {formData.dripQuantity
                                                                ? Math.round(Number(formData.dripQuantity) / 0.5)
                                                                : 0}
                                                            )
                                                        </small>

                                                    </label>
                                                    <div className="relative">
                                                        <input
                                                            id="duration"
                                                            type="number"
                                                            className="border rounded px-3 py-2 pl-8 w-full"
                                                            name="timeDuration"
                                                            min={
                                                                formData.dripQuantity
                                                                    ? Math.round(Number(formData.dripQuantity) / 7)
                                                                    : 0
                                                            }
                                                            max={
                                                                formData.dripQuantity
                                                                    ? Math.round(Number(formData.dripQuantity) / 0.5)
                                                                    : 0
                                                            }
                                                            value={
                                                                formDataDrip.timeDuration
                                                            }
                                                            onChange={
                                                                handleSelectChangeDrip
                                                            }
                                                            onKeyDown={(event) => {
                                                                const invalidKeys = ['e', 'E', '+', '-'];
                                                                if (invalidKeys.includes(event.key)) {
                                                                    event.preventDefault();
                                                                }
                                                            }}
                                                        />
                                                        <span className="absolute right-2 top-1/2 transform -translate-y-1/2">
                                                            min
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        {selectedInfusionInputDrip ===
                                            'flow_rate' && (
                                                <div className="flex flex-col mb-3 w-1/2 pl-2">
                                                    <label
                                                        htmlFor="flow_rate"
                                                        className="text-sm text-black"
                                                    >
                                                        {t(
                                                            'device_details_page.flow_rate'
                                                        )} <small>value between (0.5 - 7.0)</small>
                                                    </label>
                                                    <div className="relative">
                                                        <input
                                                            id="flow_rate"
                                                            type="number"
                                                            className="border rounded px-3 py-2 pl-8 w-full"
                                                            name="flowRate"
                                                            value={
                                                                formDataDrip.flowRate
                                                            }
                                                            min={0.5}
                                                            max={7}
                                                            step={0.5}
                                                            onChange={
                                                                handleSelectChangeDrip
                                                            }
                                                            onKeyDown={(event) => {
                                                                const invalidKeys = ['e', 'E', '+', '-'];
                                                                if (invalidKeys.includes(event.key)) {
                                                                    event.preventDefault();
                                                                }
                                                            }}
                                                        />
                                                        <span className="absolute right-2 top-1/2 transform -translate-y-1/2">
                                                            ml/min
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                    </div>
                                    <div>
                                        <button className="py-2 px-3 mx-auto w-2/6 text-white bg-primary rounded-xl flex justify-content-center items-center">
                                            {t('device_details_page.submit')}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    ) : (
                        <div className="bg-white p-4 rounded shadow-lg w-96 relative">
                            <button
                                className="absolute top-2 right-2 text-black hover:text-gray-700"
                                aria-label="close"
                                onClick={handleCloseInfusionModal}
                            >
                                <CloseIcon />
                            </button>
                            <h3 className="font-bold text-primary mb-2">
                                {t('device_details_page.infusion_settings')}
                            </h3>
                            <form onSubmit={handleSubmit}>
                                <input
                                    type="hidden"
                                    name="device_id"
                                    value={cardDetail?.device_id} // This is where you pass the hidden value
                                />
                                <div className="mx-auto flex flex-col">
                                    <label
                                        htmlFor="patient_name"
                                        className="text-sm text-black"
                                    >
                                        {t('device_details_page.patient_name')}
                                    </label>
                                    <input
                                        id="patient_name"
                                        type="text"
                                        className="form-control px-3 py-2 relative "
                                        value={formData.patientName || ''}
                                        onChange={(e) =>
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                patientName: e.target.value,
                                            }))
                                        }
                                        onBlur={() => updatePatientAndMedicineName(formData.patientName || '', formData.medicineName || '')}
                                    />

                                    <label
                                        htmlFor="volume"
                                        className="text-sm text-black mt-3"
                                    >
                                        {t('device_details_page.volume_infused')}
                                    </label>
                                    <input
                                        id="volume"
                                        type="text"
                                        className="form-control px-3 py-2 relative "
                                        value={
                                            cardDetail
                                                ? cardDetail.volume
                                                : '--'
                                        }
                                        readOnly
                                        disabled
                                    />

                                    <span className="flex items-center ml-2 absolute left-[20.5rem] top-[9.5rem]">
                                        ml
                                    </span>
                                    <label
                                        htmlFor="medicine_name"
                                        className="text-sm text-black"
                                    >
                                        {t('device_details_page.medicine_name')}
                                    </label>
                                    <input
                                        id="medicine_name"
                                        type="text"
                                        className="form-control px-3 py-2 relative "
                                        value={formData.medicineName || ''}
                                        onChange={(e) =>
                                            setFormData((prevFormData) => ({
                                                ...prevFormData,
                                                medicineName: e.target.value,
                                            }))
                                        }
                                        onBlur={() => updatePatientAndMedicineName(formData.patientName || '', formData.medicineName || '')}
                                    />

                                    <label
                                        htmlFor="infusion_select"
                                        className="text-sm text-black mt-3"
                                    >
                                        {t(
                                            'device_details_page.infusion_select'
                                        )}
                                    </label>
                                    <select
                                        id="infusion_select"
                                        className="border rounded px-3 py-2"
                                        name="type"
                                        value={formData.type}
                                        onChange={handleSelectChange}
                                    >
                                        <option value="">
                                            {t('device_details_page.select')}
                                        </option>
                                        <option value="time_duration">
                                            {t(
                                                'device_details_page.time_infused'
                                            )}
                                        </option>
                                        <option value="flow_rate">
                                            {t('device_details_page.flow_rate')}
                                        </option>
                                        <option value="drip_quantity">
                                            {t(
                                                'device_details_page.drip_quantity'
                                            )}
                                        </option>
                                        <option value="hourly_flow_rate">
                                            {t(
                                                'device_details_page.hourly_flow_rate'
                                            )}
                                        </option>
                                    </select>

                                    {selectedInfusionInput ===
                                        'hourly_flow_rate' && (
                                            <div className="flex flex-col mt-3 mb-2 bg-slate-50 p-2.5 rounded border border-slate-200">
                                                <div className="flex justify-between items-center mb-2">
                                                    <span className="text-xs font-semibold text-slate-700">
                                                        Set Schedule Settings
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={handleAddHourlyStep}
                                                        className="text-[11px] bg-primary text-white px-2 py-0.5 rounded hover:bg-opacity-90 font-medium"
                                                    >
                                                        {t('device_details_page.add_hour')}
                                                    </button>
                                                </div>
                                                <div className="max-h-48 overflow-y-auto space-y-2 pr-1">
                                                    {(formData.hourlySteps || [{ hour: 1, setMins: 60, flowRate: 2.0 }]).map((step, index) => {
                                                        const stepVol = (Number(step.setMins || 0) * Number(step.flowRate || 0)).toFixed(1);
                                                        return (
                                                            <div key={index} className="flex flex-col gap-1 bg-white p-1.5 rounded border border-slate-200 text-xs">
                                                                <div className="flex items-center justify-between font-semibold text-slate-700 text-[11px]">
                                                                    <span>{t('device_details_page.hour')} {step.hour}:</span>
                                                                    <span className="text-slate-500 font-normal text-[10px]">
                                                                        {t('device_details_page.set_volume')}: <strong className="text-slate-800">{stepVol} ml</strong>
                                                                    </span>
                                                                </div>
                                                                <div className="flex items-center gap-1.5">
                                                                    <div className="flex flex-col flex-1 min-w-0">
                                                                        <label className="text-[10px] text-gray-500">{t('device_details_page.duration_mins')}</label>
                                                                        <input
                                                                            type="number"
                                                                            className="border rounded px-1.5 py-0.5 w-full text-xs"
                                                                            value={step.setMins}
                                                                            min={1}
                                                                            onChange={(e) => handleHourlyStepChange(index, 'setMins', Math.max(1, Number(e.target.value)))}
                                                                        />
                                                                    </div>
                                                                    <div className="flex flex-col flex-1 min-w-0">
                                                                        <label className="text-[10px] text-gray-500">{t('device_details_page.flow_rate')} (ml/min)</label>
                                                                        <input
                                                                            type="number"
                                                                            className="border rounded px-1.5 py-0.5 w-full text-xs"
                                                                            value={step.flowRate}
                                                                            min={0.5}
                                                                            max={7.0}
                                                                            step={0.5}
                                                                            onChange={(e) => handleHourlyStepChange(index, 'flowRate', Number(e.target.value))}
                                                                        />
                                                                    </div>
                                                                    {(formData.hourlySteps || []).length > 1 && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleRemoveHourlyStep(index)}
                                                                            className="text-red-500 hover:text-red-700 font-bold px-1 py-0.5 text-xs shrink-0 self-end mb-0.5"
                                                                            title="Remove Step"
                                                                        >
                                                                            ✕
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                                {(() => {
                                                    const steps = formData.hourlySteps || [{ hour: 1, setMins: 60, flowRate: 2.0 }];
                                                    const totalMins = steps.reduce((acc, curr) => acc + Number(curr.setMins || 0), 0);
                                                    const totalProgVol = steps.reduce((acc, curr) => acc + Number(curr.setMins || 0) * Number(curr.flowRate || 0), 0);
                                                    const vtbi = Number(cardDetail?.Sel_Qua || cardDetail?.volume || 0);
                                                    const remVol = vtbi > 0 ? Math.max(0, vtbi - totalProgVol) : 0;
                                                    return (
                                                        <div className="mt-2 flex flex-col gap-1 text-[11px] font-medium text-slate-600 bg-white p-1.5 rounded border border-slate-200">
                                                            <div className="flex justify-between">
                                                                <span>{t('device_details_page.total_set_mins')}:</span>
                                                                <span className="text-slate-900 font-bold">{totalMins} mins</span>
                                                            </div>
                                                            <div className="flex justify-between">
                                                                <span>{t('device_details_page.programmed_volume')}:</span>
                                                                <span className="text-primary font-bold">{totalProgVol.toFixed(1)} ml</span>
                                                            </div>
                                                            {vtbi > 0 && (
                                                                <div className="flex justify-between border-t pt-1">
                                                                    <span>{t('device_details_page.remaining_volume')}:</span>
                                                                    <span className={`font-bold ${remVol < 0 ? 'text-red-500' : 'text-green-600'}`}>
                                                                        {remVol.toFixed(1)} / {vtbi} ml
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        )}

                                    {selectedInfusionInput ===
                                        'time_duration' && (
                                            <>
                                                <label
                                                    htmlFor="duration"
                                                    className="text-sm text-black mt-3"
                                                >
                                                    {t(
                                                        'device_details_page.time_infused'
                                                    )} <small>value between ({cardDetail?.volume ? Math.round(cardDetail.volume / 7) : 0} - {cardDetail?.volume ? cardDetail.volume : 0})</small>
                                                </label>
                                                <input
                                                    id="duration"
                                                    type="number"
                                                    className="border rounded px-3 py-2 relative"
                                                    name="timeDuration"
                                                    min={cardDetail?.volume ? Math.round(cardDetail.volume / 7) : 0}
                                                    max={cardDetail?.volume ? cardDetail.volume : 0}
                                                    value={
                                                        formData.timeDuration
                                                    }
                                                    onChange={
                                                        handleSelectChange
                                                    }
                                                    onKeyDown={(event) => {
                                                        const invalidKeys = ['e', 'E', '+', '-'];
                                                        if (invalidKeys.includes(event.key)) {
                                                            event.preventDefault();
                                                        }
                                                    }}
                                                />
                                                <span className="flex items-center ml-2 absolute left-[20.2rem] top-[18.25rem]">
                                                    min
                                                </span>
                                            </>
                                        )}
                                    {selectedInfusionInput === 'flow_rate' && (
                                        <>
                                            <label
                                                htmlFor="flow_rate"
                                                className="text-sm text-black mt-3"
                                            >
                                                {t(
                                                    'device_details_page.flow_rate'
                                                )} <small>value between (0.5 - 7.0)</small>
                                            </label>
                                            <input
                                                id="flow_rate"
                                                type="number"
                                                className="border rounded px-3 py-2 relative"
                                                name="flowRate"
                                                value={
                                                    formData.flowRate
                                                }
                                                min={0.5}
                                                max={7}
                                                step={0.5}
                                                onChange={
                                                    handleSelectChange
                                                }
                                                onKeyDown={(event) => {
                                                    const invalidKeys = ['e', 'E', '+', '-'];
                                                    if (invalidKeys.includes(event.key)) {
                                                        event.preventDefault();
                                                    }
                                                }}
                                            />
                                            <span className="flex items-center ml-2 absolute left-[18.25rem] top-[18.25rem]">
                                                ml/min
                                            </span>
                                        </>
                                    )}
                                    {selectedInfusionInput ===
                                        'drip_quantity' && (
                                            <>
                                                <label
                                                    htmlFor="drip_quantity"
                                                    className="text-sm text-black mt-3"
                                                >
                                                    {t(
                                                        'device_details_page.drip_quantity'
                                                    )} <small>value between (60 - {cardDetail?.volume})</small>
                                                </label>
                                                <input
                                                    id="drip_quantity"
                                                    type="number"
                                                    className="border rounded px-3 py-2 relative mb-2"
                                                    name="dripQuantity"
                                                    min={60}
                                                    max={cardDetail?.volume}
                                                    value={
                                                        formData.dripQuantity
                                                    }
                                                    onChange={
                                                        handleInputChange
                                                    }
                                                    onKeyDown={(event) => {
                                                        const invalidKeys = ['e', 'E', '+', '-'];
                                                        if (invalidKeys.includes(event.key)) {
                                                            event.preventDefault();
                                                        }
                                                    }}
                                                />
                                                <span className="flex items-center ml-2 absolute left-[19.25rem] top-[18.25rem]">
                                                    gtt/ml
                                                </span>
                                            </>
                                        )}
                                    {selectedInfusionInput ===
                                        'drip_quantity' && (
                                            <>
                                                <label className="text-sm text-black">
                                                    {t(
                                                        'device_details_page.select_drip'
                                                    )}
                                                </label>
                                                <select
                                                    aria-label="select"
                                                    className="border rounded px-3 py-2 mb-2"
                                                    name="type"
                                                    value={formDataDrip.type}
                                                    onChange={
                                                        handleSelectChangeDrip
                                                    }
                                                >
                                                    <option value="">{t('device_details_page.select')}</option>
                                                    <option value="time_duration">
                                                        {t(
                                                            'device_details_page.time_infused'
                                                        )}
                                                    </option>
                                                    <option value="flow_rate">
                                                        {t(
                                                            'device_details_page.flow_rate'
                                                        )}
                                                    </option>
                                                </select>
                                            </>
                                        )}
                                    {selectedInfusionInputDrip ===
                                        'time_duration' && (
                                            <>
                                                <label
                                                    htmlFor="duration"
                                                    className="text-sm text-black"
                                                >
                                                    {t(
                                                        'device_details_page.time_infused'
                                                    )} <small>
                                                        value between (
                                                        {formData.dripQuantity
                                                            ? Math.round(Number(formData.dripQuantity) / 7)
                                                            : 0}
                                                        {' - '}
                                                        {formData.dripQuantity
                                                            ? Math.round(Number(formData.dripQuantity) / 0.5)
                                                            : 0}
                                                        )
                                                    </small>
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        id="duration"
                                                        type="number"
                                                        className="border rounded px-3 py-2 pl-8 w-full mb-2"
                                                        name="timeDuration"
                                                        min={
                                                            formData.dripQuantity
                                                                ? Math.round(Number(formData.dripQuantity) / 7)
                                                                : 0
                                                        }
                                                        max={
                                                            formData.dripQuantity
                                                                ? Math.round(Number(formData.dripQuantity) / 0.5)
                                                                : 0
                                                        }
                                                        value={
                                                            formDataDrip.timeDuration
                                                        }
                                                        onChange={
                                                            handleSelectChangeDrip
                                                        }
                                                        onKeyDown={(event) => {
                                                            const invalidKeys = ['e', 'E', '+', '-'];
                                                            if (invalidKeys.includes(event.key)) {
                                                                event.preventDefault();
                                                            }
                                                        }}
                                                    />
                                                    <span className="absolute right-2 top-1/2 transform -translate-y-1/2">
                                                        min
                                                    </span>
                                                </div>
                                            </>
                                        )}
                                    {selectedInfusionInputDrip ===
                                        'flow_rate' && (
                                            <>
                                                <label
                                                    htmlFor="flow_rate"
                                                    className="text-sm text-black"
                                                >
                                                    {t(
                                                        'device_details_page.flow_rate'
                                                    )} <small>value between (0.5 - 7.0)</small>
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        id="flow_rate"
                                                        type="number"
                                                        className="border rounded px-3 py-2 pl-8 w-full mb-2"
                                                        name="flowRate"
                                                        value={
                                                            formDataDrip.flowRate
                                                        }
                                                        min={0.5}
                                                        max={7}
                                                        step={0.5}
                                                        onChange={
                                                            handleSelectChangeDrip
                                                        }
                                                        onKeyDown={(event) => {
                                                            const invalidKeys = ['e', 'E', '+', '-'];
                                                            if (invalidKeys.includes(event.key)) {
                                                                event.preventDefault();
                                                            }
                                                        }}
                                                    />
                                                    <span className="absolute right-2 top-1/2 transform -translate-y-1/2">
                                                        ml/min
                                                    </span>
                                                </div>
                                            </>
                                        )}

                                    <div>
                                        <button className="py-2 px-3 mx-auto w-4/5 text-white bg-primary mt-4 rounded-xl flex justify-content-center items-center">
                                            {t('device_details_page.submit')}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    )}
                </div>
            )}
            <DeviceModalPopUp
                show={showTextModal}
                message={t('pop_up.device_stopped_msg')}
                message1={t('pop_up.device_stopped_msg1')}
                icon={<GoAlertFill size={40} className="text-red" />}
                onConfirm={() => {
                    setShowTextModal(true);
                }}
                onCancel={() => setShowTextModal(false)}
            />
            <ModalPopUp
                show={showModal}
                message={t('pop_up.take_over')}
                onConfirm={() => {
                    updateDeviceControl();
                }}
                onCancel={() => {
                    setShowModal(false);
                    setIsFirstModalOpen(true);
                }}
            />
            {loading && <DataLoader />}

            {infusionLoading && (
                <div className="absolute inset-0 flex justify-center items-center bg-black bg-opacity-75 z-50">
                    <Spinner animation="border" role="status" variant="primary">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            )}
        </div>
    );
};
export default DeviceListDetailPage;
